function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import ServiceFactory from "../../service";
import { INPUT } from "../../components/form-styles";
import { PiecePreview, recipeOf, parseJson, channelLabel, CHANNEL_ICON, OWNED, mediaRowFor, mediaPromptFromRecipe } from "./templates";
import { TemplateSheet } from "./templates";
import { EditModeModal, SmartResizeModal } from "./edit-mode";
import { TrimModal, VideoEditModal, VideoResizeModal } from "./video-edit";
import { runAgent, waitAgent, runIdOf } from "./agent-run";
import { VoicePicker } from "./voices";
import { wordDiff, nameOf, initialsOf } from "./collab";
import { NotesPanel, PeopleRow, AssigneeDots, OwnerPicker } from "./notes";
import { ReviewBox, RequestReviewSheet } from "./review";

// Builder · Make (CONTENT_HUB_TEMPLATES spec, "Make on the template", DS 2026-10-02): Plan, Write and Media folded into one stage.
// Each planned piece is a canvas: the template rendered with the story's copy and images. Slots are live: text slots write
// or rewrite from the story, image slots pick from Assets or generate, video pieces storyboard and render. Approve on the
// canvas; Ship previews the same thing. The Builder instance comes in as `b` so every existing rail is reused as-is.
// House rules: kit tokens, no credit copy, no vertical hard-coded.

function svc(api) {
  const S = ServiceFactory.getType(api, "product");
  return new S();
}
async function safe(p, fb) {
  try {
    return (await p) || fb;
  } catch (e) {
    return fb;
  }
}
const toast = m => {
  const t = document.querySelector("paper-toast");
  if (t && m) t.show(m);
};
const strip = h => String(h || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
// Body as readable text for Compare: paragraphs and line breaks kept, tags dropped.
const plainBody = h => String(h || "").replace(/<(br|\/p|\/div|\/li|\/h\d)[^>]*>/gi, "\n").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/[ \t]+/g, " ").replace(/\n\s*\n+/g, "\n\n").trim();
const PAGE_CHANNELS = new Set(["landing", "blog", "web"]);
const escAttr = x => String(x || "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
/** Site frame (DS 2026-10-02): the website's own head styles, header, nav and footer around a page. Canvas mode keeps the frame inert. */
const wrapFrame = (html, fr, canvasMode) => {
  if (!fr || !html) return html;
  const head = (html.match(/<head[^>]*>([\s\S]*?)<\/head>/i) || [])[1] || "";
  const bm = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  const body = bm ? bm[1] : html.replace(/<!doctype[^>]*>|<\/?html[^>]*>|<head[\s\S]*?<\/head>/gi, "");
  const guard = canvasMode ? "<style>.plk-site-frame{pointer-events:none}</style>" : "";
  return `<!doctype html><html class="${escAttr(fr.htmlClass)}"><head><meta charset="utf-8"><base href="${escAttr(fr.baseUrl)}">${fr.head || ""}${head}${guard}</head><body class="${escAttr(fr.bodyClass)}" style="${escAttr(fr.bodyStyle)}"><div class="plk-site-frame">${fr.header || ""}</div><main class="plk-page">${body}</main><div class="plk-site-frame">${fr.footer || ""}</div></body></html>`;
};
const forVariant = v => x => {
  try {
    return String(JSON.parse(x.body || "{}").variantId) === String(v.id);
  } catch (e) {
    return false;
  }
};
const latestBlock = (blocks, kind, test) => (blocks || []).filter(x => String(x.kind || "") === kind && (!test || test(x))).sort((p, q) => (Number(q.version) || 0) - (Number(p.version) || 0))[0];
const TEXT_TYPES = new Set(["hero", "heading", "text", "imagetext", "features", "quote", "offer", "button", "specs"]);
const IMAGE_TYPES = new Set(["hero", "imagetext", "gallery"]);
// Generate popover choices: one hero shot, or a set that fills the slot and leaves the rest under Pick image
const GEN_KINDS = [["hero", "One picture", 1], ["lifestyle", "Lifestyle set", 3], ["sceneset", "Scene set", 4]];
const CHANNEL_KEYS = ["product_copy", "blog", "landing", "email", "social_instagram", "social_facebook", "social_pinterest", "social_linkedin", "gbp_post", "ad"];
// Plan row keys: d:<channel> for the first piece of a channel, d:<channel>:<n> for a second or third (DS 2026-10-02: video post + image post on one network).
const pieceOrdinal = key => {
  const m = String(key || "").match(/^d:[^:]+:(\d+)$/);
  return m ? Number(m[1]) : 1;
};
// Plain names for what a piece is, and the noun the Approve button uses ("Approve this email").
const KIND = {
  email: ["Email", "email"],
  landing: ["Web page", "page"],
  web: ["Web page", "page"],
  blog: ["Blog post", "blog post"],
  product_copy: ["Product description", "description"],
  ad: ["Ad", "ad"],
  social_instagram: ["Instagram post", "post"],
  social_facebook: ["Facebook post", "post"],
  social_pinterest: ["Pinterest pin", "pin"],
  social_linkedin: ["LinkedIn post", "post"],
  gbp_post: ["Google Business post", "post"],
  video: ["Video", "video"]
};
const kindOf = p => p.kind === "video" ? KIND.video : KIND[p.channel] || [channelLabel(p.channel), "piece"];
/** A layout or video name as people read it: slug-like names get spaces and a capital. */
const humanName = n => {
  let x = String(n || "").trim();
  if (!x) return "";
  if (!/\s/.test(x)) x = x.replace(/[_-]+/g, " ");
  return x.charAt(0).toUpperCase() + x.slice(1);
};
const SEO_CHANNELS = new Set(["blog", "landing", "web", "website", "product_copy"]);
const MEDIA_REQUIRED = new Set(["social_instagram", "social_pinterest"]);
// Status chip per piece: [label, tone]
const STATUS_CHIP = {
  rv_in_review: ["In review", "info"],
  rv_changes: ["Changes asked", "warn"],
  rv_signed: ["Signed off", "ok"],
  approved: ["Approved", "ok"],
  published: ["Published", "ok"],
  review: ["Review", "warn"],
  writing: ["Writing…", "busy"],
  empty: ["Not written", "idle"],
  skipped: ["Skipped", "idle"],
  ready: ["Made", "ok"],
  making: ["Making…", "busy"],
  unmade: ["Not made", "idle"]
};
const SLOT_LABEL = {
  hero: "Hero",
  heading: "Heading",
  text: "Text",
  imagetext: "Image + text",
  gallery: "Gallery",
  features: "Feature list",
  quote: "Quote",
  offer: "Offer",
  button: "Button",
  specs: "Specs",
  products: "Products",
  recommended: "Recommended",
  divider: "Divider"
};

/** Pick a frame from a rendered clip as its thumbnail: scrub, capture the frame, upload it, record it as the asset's poster. */
class FramePicker extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      t: 0,
      dur: 0,
      busy: false,
      tainted: false
    });
    _defineProperty(this, "vid", /*#__PURE__*/React.createRef());
    _defineProperty(this, "capture", async () => {
      const v = this.vid.current;
      if (!v) return;
      this.setState({
        busy: true
      });
      try {
        const c = document.createElement("canvas");
        c.width = v.videoWidth || 1280;
        c.height = v.videoHeight || 720;
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        const blob = await new Promise((ok, no) => {
          try {
            c.toBlob(b2 => b2 ? ok(b2) : no(new Error("no frame")), "image/jpeg", 0.9);
          } catch (e) {
            no(e);
          }
        });
        const up = await svc("/content/assets/upload-url").postAsync({
          fileName: "thumbnail.jpg",
          contentType: "image/jpeg",
          bytes: blob.size
        });
        if (!up || !up.uploadUrl) throw new Error("no upload url");
        const put = await fetch(up.uploadUrl, {
          method: "PUT",
          headers: {
            "content-type": "image/jpeg"
          },
          body: blob
        });
        if (!put.ok) throw new Error(`upload failed (${put.status})`);
        await this.props.onPoster(up.url);
      } catch (e) {
        if (/tainted|SecurityError|insecure/i.test(String(e && e.name + " " + e.message))) {
          this.setState({
            tainted: true
          });
          toast("This clip cannot be captured in the browser: pick an image as the cover instead.");
        } else toast(e && e.message || "Could not save the frame.");
      } finally {
        if (this.vid.current) this.setState({
          busy: false
        });
      }
    });
  }
  render() {
    const {
      url,
      onClose,
      onPickImage
    } = this.props;
    const {
      t,
      dur,
      busy,
      tainted
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pop wide hub-make-frame-pick",
      style: {
        top: 8,
        left: 14
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pop-head"
    }, /*#__PURE__*/React.createElement("b", null, "Thumbnail"), /*#__PURE__*/React.createElement("span", null, "Scrub to the frame you want, then use it"), /*#__PURE__*/React.createElement("button", {
      className: "material-icons",
      title: "Close",
      onClick: onClose
    }, "close")), /*#__PURE__*/React.createElement("video", {
      ref: this.vid,
      src: url,
      crossOrigin: "anonymous",
      muted: true,
      playsInline: true,
      preload: "auto",
      onLoadedMetadata: e => this.setState({
        dur: e.target.duration || 0
      })
    }), /*#__PURE__*/React.createElement("input", {
      type: "range",
      min: 0,
      max: dur || 0,
      step: 0.1,
      value: t,
      onChange: e => {
        const x = Number(e.target.value);
        this.setState({
          t: x
        });
        if (this.vid.current) this.vid.current.currentTime = x;
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: "hub-make-frame-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, dur ? `${t.toFixed(1)}s of ${dur.toFixed(1)}s` : "loading…"), /*#__PURE__*/React.createElement("span", {
      style: {
        flex: 1
      }
    }), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle sm",
      style: {
        margin: 0
      },
      onClick: onPickImage
    }, "Use an image instead"), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary",
      disabled: busy || !dur || tainted,
      onClick: this.capture
    }, busy ? "Saving…" : "Use this frame")));
  }
}

/** Media picker (DS 2026-10-02): the story's pool first, the whole Assets library on demand; search, kind chips, hover labels, newest first. */
class MediaPicker extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      q: "",
      kind: this.props.initialKind || "all",
      scope: this.props.initialScope || "story",
      story: null,
      lib: null
    });
    _defineProperty(this, "_alive", true);
    _defineProperty(this, "norm", (a, extra) => ({
      id: a.id ? String(a.id) : null,
      url: a.url,
      kind: String(a.kind || "image").toLowerCase(),
      title: a.title || "",
      alt: a.altText || "",
      source: String(a.source || ""),
      poster: a.posterUrl || null,
      w: a.width,
      h: a.height,
      dur: a.durationSeconds,
      at: a.createdDate || "",
      ...(extra || {})
    }));
    _defineProperty(this, "loadStory", async () => {
      const b = this.props.b;
      const sid = String(b.state.storyId || b.state.primary && (b.state.primary.storyId || b.state.primary.id) || "");
      const r = sid ? await safe(svc(`/content/stories/${sid}/assets?$top=120`).getAsync(), null) : null;
      const items = (r && r.items || []).map(a => this.norm(a));
      const seen = new Set(items.map(x => x.url));
      b.storyImages().forEach(u => {
        if (u && !seen.has(u)) {
          seen.add(u);
          items.push({
            id: null,
            url: u,
            kind: "image",
            title: "",
            alt: "",
            source: "block",
            poster: null,
            at: ""
          });
        }
      });
      b.renderedVideos().forEach(({
        v,
        url
      }) => {
        if (url && !seen.has(url)) {
          seen.add(url);
          items.push({
            id: null,
            url,
            kind: "video",
            title: v.title || "Video",
            alt: "",
            source: "plunk_video",
            poster: b.posterFor ? b.posterFor(url) : null,
            at: ""
          });
        }
      });
      if (this._alive) this.setState({
        story: items
      });
    });
    _defineProperty(this, "loadLib", async () => {
      if (this.state.lib) return;
      const r = await safe(svc(`/content/assets?$top=200`).getAsync(), null);
      if (this._alive) this.setState({
        lib: (r && r.items || []).map(a => this.norm(a))
      });
    });
  }
  componentDidMount() {
    this._alive = true;
    this.loadStory();
    if (this.state.scope === "all") this.loadLib();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  items() {
    const {
      q,
      kind,
      scope,
      story,
      lib
    } = this.state;
    let list = scope === "all" ? (lib || []).concat((story || []).filter(x => !(lib || []).some(y => y.url === x.url))) : story || [];
    if (!this.props.allowVideo) list = list.filter(x => x.kind !== "video");
    if (kind === "image" || kind === "video") list = list.filter(x => x.kind === kind);else if (kind === "generated") list = list.filter(x => /^plunk_/.test(x.source));else if (kind === "uploaded") list = list.filter(x => x.source && !/^plunk_/.test(x.source) && x.source !== "block");
    const needle = q.trim().toLowerCase();
    if (needle) list = list.filter(x => `${x.title} ${x.alt} ${x.source}`.toLowerCase().includes(needle));
    return list.slice().sort((a, b) => String(b.at).localeCompare(String(a.at)));
  }
  render() {
    const {
      current,
      onPick,
      drafts,
      useOn
    } = this.props;
    const {
      q,
      kind,
      scope,
      story
    } = this.state;
    const list = this.items();
    const src = x => x.source === "block" ? "on the piece" : /^plunk_image/.test(x.source) ? "generated" : /^plunk_video/.test(x.source) ? "rendered" : /^plunk_/.test(x.source) ? x.source.replace(/^plunk_/, "").replace(/_/g, " ") : x.source || "uploaded";
    const meta = x => [x.w && x.h ? `${x.w}×${x.h}` : "", x.dur ? `${x.dur}s` : "", src(x)].filter(Boolean).join(" · ");
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-mp"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-mp-bar"
    }, /*#__PURE__*/React.createElement("input", {
      value: q,
      placeholder: "Search by title or alt text",
      onChange: e => this.setState({
        q: e.target.value
      }),
      style: {
        ...INPUT,
        height: "30px",
        padding: "0 9px",
        flex: 1,
        minWidth: 120
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: "hub-mp-seg"
    }, [["story", "This story"], ["all", "All assets"]].map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      className: "territory-toggle xs" + (scope === k ? " active" : ""),
      onClick: () => {
        this.setState({
          scope: k
        });
        if (k === "all") this.loadLib();
      }
    }, l)))), /*#__PURE__*/React.createElement("div", {
      className: "hub-mp-chips"
    }, [["all", "All"], ["image", "Images"], ...(this.props.allowVideo ? [["video", "Videos"]] : []), ["generated", "Generated"], ["uploaded", "Uploaded"]].map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      className: "territory-toggle xs" + (kind === k ? " on" : ""),
      onClick: () => this.setState({
        kind: k
      })
    }, l)), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: "0 0 0 auto"
      }
    }, story ? `${list.length} shown` : "loading…")), /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pick hub-mp-grid"
    }, list.map(x => /*#__PURE__*/React.createElement("span", {
      key: x.url,
      className: "hub-make-pickitem" + (current && current === x.url ? " current" : ""),
      title: [x.title || x.alt, meta(x)].filter(Boolean).join(" · ")
    }, x.kind === "video" ? /*#__PURE__*/React.createElement("video", {
      src: x.url,
      poster: x.poster || undefined,
      muted: true,
      playsInline: true,
      preload: "metadata",
      onClick: () => onPick(x)
    }) : /*#__PURE__*/React.createElement("img", {
      src: x.url,
      alt: "",
      loading: "lazy",
      onClick: () => onPick(x)
    }), /*#__PURE__*/React.createElement("span", {
      className: "lbl"
    }, /*#__PURE__*/React.createElement("b", null, x.title || x.alt || (x.kind === "video" ? "Video" : "Image")), /*#__PURE__*/React.createElement("i", null, meta(x))), x.kind === "video" && /*#__PURE__*/React.createElement("span", {
      className: "material-icons vid"
    }, "play_circle"), current && current === x.url && /*#__PURE__*/React.createElement("span", {
      className: "material-icons cur"
    }, "check_circle"), useOn && drafts && drafts.length > 0 && x.kind !== "video" && /*#__PURE__*/React.createElement("select", {
      className: "hub-make-useon",
      value: "",
      title: "Use this image on another piece",
      onClick: e => e.stopPropagation(),
      onChange: e => {
        const k = e.target.value;
        if (k) useOn(x.url, k);
      }
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "Use on\u2026"), drafts.map(d => /*#__PURE__*/React.createElement("option", {
      key: d.key,
      value: d.key
    }, d.label))))), story && !list.length && /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0,
        gridColumn: "1 / -1"
      }
    }, q ? "Nothing matches." : scope === "story" ? "No media on this story yet. Generate one, or look in All assets." : "No assets yet.")));
  }
}
export default class MakeStage extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      sel: null,
      canvas: {},
      busy: {},
      edit: null,
      pick: null,
      gen: null,
      angleDraft: null,
      mediaEdit: null,
      hover: null,
      frames: {},
      frameEdit: null,
      frameOn: true,
      addOpen: false,
      rowMenu: null,
      allBusy: false,
      ask: {},
      open: {},
      partsOpen: null,
      assetIds: {},
      checks: {},
      checking: null
    });
    _defineProperty(this, "_iframes", {});
    _defineProperty(this, "savePieceMap", async map => {
      const b = this.props.b;
      await safe(svc(`/content/containers/${String(b.state.primary.id)}/blocks`).postAsync({
        kind: "piece_map",
        body: JSON.stringify(map)
      }), null);
    });
    _defineProperty(this, "select", async p => {
      this.setState({
        sel: p.key,
        edit: null,
        pick: null,
        gen: null,
        hover: null,
        angleDraft: null,
        partsOpen: null,
        rowMenu: null
      });
      await this.refreshCanvas(p);
    });
    _defineProperty(this, "captureFrame", async url => {
      const b = this.props.b;
      const u = String(url || "").trim();
      if (!/^https?:\/\//i.test(u)) return;
      this.setState({
        frameEdit: {
          ...this.state.frameEdit,
          url: u,
          busy: true
        }
      });
      try {
        const r = await runAgent("content_capture_agent", `Capture the site frame of ${u}`, {
          url: u,
          frame: "true",
          width: "1440"
        });
        const done = await waitAgent(runIdOf(r), 4000, 30, () => this._alive);
        const frameUrl = done.ok && (done.ctx && done.ctx.frameUrl || done.res && done.res.frameUrl || "");
        if (!/^https?:\/\//i.test(String(frameUrl))) {
          const why = done.run && (done.run.runResult || done.run.RunResult);
          toast(done.timeout ? "The site is still being read: try again in a minute." : `Could not read the site${why ? `: ${String(why).slice(0, 120)}` : ""}.`);
          return;
        }
        const host = u.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
        await safe(svc(`/content/containers/${String(b.state.primary.id)}/blocks`).postAsync({
          kind: "site_frame",
          body: JSON.stringify({
            url: u,
            host,
            frameUrl,
            title: done.ctx && done.ctx.title || "",
            capturedAt: new Date().toISOString()
          })
        }), null);
        await b.reloadDetails();
        toast(`Site frame read from ${host}.`);
      } finally {
        if (this._alive) this.setState({
          frameEdit: {
            ...this.state.frameEdit,
            busy: false
          },
          frameOn: true
        });
      }
    });
    _defineProperty(this, "removeFrame", async () => {
      const b = this.props.b;
      await safe(svc(`/content/containers/${String(b.state.primary.id)}/blocks`).postAsync({
        kind: "site_frame",
        body: JSON.stringify({
          removed: true,
          frameUrl: ""
        })
      }), null);
      await b.reloadDetails();
      if (this._alive) this.setState({
        frameEdit: null
      });
    });
    /** Write the piece's parts from the story. slots = only those parts (null = all); note = the reader's "Ask for changes" text. */
    _defineProperty(this, "fill", async (p, slots, note) => {
      const b = this.props.b;
      const v = this.variantFor(p);
      const d = this.designFor(p, v);
      if (!d) return;
      const key = `fill:${p.key}`;
      this.setBusy(key, true);
      try {
        const body = JSON.stringify({
          design: d,
          slots: slots || null,
          angle: [p.row.angle, note].filter(Boolean).join(" "),
          title: v ? v.title : ""
        }).replace(/}$/, `,"containerId":${String(b.state.primary.id)}}`);
        const r = await svc("/content/templates/fill").postAsync(body);
        if (!r || !r.design) {
          toast("Plunk could not write that. Try again.");
          return;
        }
        const design = typeof r.design === "string" ? parseJson(r.design) : r.design;
        if (design.frame !== "page") {
          await this.persist(p, design, r.html, null);
        } else {
          const rr = await safe(svc("/content/templates/render").postAsync(JSON.stringify({
            design,
            mode: "final"
          })), null);
          await this.persist(p, design, rr && rr.html || r.html, rr && rr.body);
        }
        toast(slots ? slots.length === 1 ? "That part is written." : "The empty parts are written." : "Rewritten from the story.");
      } catch (e) {
        b.agentError(e, "Plunk could not write that.");
      } finally {
        if (this._alive) this.setBusy(key, false);
      }
    });
    _defineProperty(this, "saveSlot", async (p, i, patch) => {
      const v = this.variantFor(p);
      const d = this.designFor(p, v);
      if (!d) return;
      const design = {
        ...d,
        sections: d.sections.map((s, k) => k === i ? {
          ...s,
          ...patch
        } : s)
      };
      const key = `slot:${p.key}`;
      this.setBusy(key, true);
      try {
        const rr = await safe(svc("/content/templates/render").postAsync(JSON.stringify({
          design,
          mode: "final"
        })), null);
        if (!rr || !rr.html) {
          toast("Could not update the preview.");
          return;
        }
        await this.persist(p, design, rr.html, rr.body);
        this.setState({
          edit: null,
          pick: null
        });
      } finally {
        if (this._alive) this.setBusy(key, false);
      }
    });
    // Generate popover (replaces the Story images panel): a hero shot, or a set. Every shot lands in the story pool (Assets);
    // the first fills the slot (a gallery takes up to three), the rest wait under Pick image. i = -1 is a social piece.
    _defineProperty(this, "generateImage", async (p, i, opts) => {
      const b = this.props.b;
      const t = this.templateFor(p);
      const media = t ? recipeOf(t).media : {};
      const kind = opts && opts.kind || "hero";
      const count = (GEN_KINDS.find(k => k[0] === kind) || [0, 0, 1])[2];
      const v = this.variantFor(p);
      const d = i >= 0 ? this.designFor(p, v) : null;
      const sec = d ? d.sections[i] : null;
      const row = {
        key: `slot:${p.key}:${i}`,
        media: kind,
        aspect: media.aspect || (i >= 0 ? "16:9" : "1:1"),
        prompt: [sec && sec.prompt, opts && opts.direction, media.scene ? `${String(media.scene).replace(/_/g, " ")} scene` : ""].filter(Boolean).join(". "),
        templateId: p.row.templateId
      };
      this.setState({
        gen: null
      });
      const urls = [];
      for (let k = 0; k < count; k++) {
        await b.makeImage(row, k);
        const u = b.state.mediaOut[`${row.key}:${k}`];
        if (u) urls.push(u);
      }
      if (!urls.length) return;
      if (sec) await this.saveSlot(p, i, String(sec.type).toLowerCase() === "gallery" ? {
        images: [...(sec.images || []), ...urls].slice(-3)
      } : {
        imageUrl: urls[0],
        imageFrom: "url"
      });else if (v) await b.setSocialMedia(v, {
        imageUrl: urls[0]
      });
      if (count > 1) toast(`${urls.length} pictures made: the first is on the piece, the rest are under Pick a picture.`);
    });
    _defineProperty(this, "openGen", (p, i) => this.setState({
      gen: this.state.gen && this.state.gen.key === p.key && this.state.gen.i === i ? null : {
        key: p.key,
        i,
        kind: "hero",
        direction: ""
      },
      pick: null
    }));
    _defineProperty(this, "approve", async p => {
      const v = this.variantFor(p);
      if (!v) {
        toast("Write the piece first.");
        return;
      }
      await this.props.b.setVariantStatus(v, "approved");
    });
    // Change template from the canvas: the plan row gets the recipe (angle, media prompt) and the Brief's pick follows.
    _defineProperty(this, "setTemplate", (p, templateId, opts) => {
      const b = this.props.b;
      // "Add a reference…": paste copy, an image or a URL and a template is extracted from it, then offered here.
      if (templateId === "__ref") {
        b.setState({
          tplSheet: {
            kind: "reference",
            channel: p.kind === "video" ? "video" : p.channel
          }
        });
        return;
      }
      const t = templateId ? b.tpl(templateId) : null;
      // A filled page or email: preview the new layout with the current copy poured in before anything changes (DS 2026-10-02).
      if (p.kind !== "video" && OWNED.has(p.channel) && t && !(opts && opts.apply)) {
        const v = this.variantFor(p);
        const cur = this.designFor(p, v);
        const filled = cur && cur.sections.some(x => x.headline || x.html || x.items && x.items.length || x.imageUrl || x.images && x.images.length);
        if (filled) {
          this.previewTemplate(p, t, cur);
          return;
        }
      }
      const plan = (b.state.plan || []).map(r => r.key === p.key ? {
        ...r,
        templateId: t ? String(t.id) : null,
        ...(p.kind === "video" && t ? {
          label: t.name,
          aspect: (recipeOf(t).media || {}).aspect || r.aspect,
          media: mediaRowFor(t) || r.media,
          prompt: mediaPromptFromRecipe(t)
        } : {})
      } : r);
      const brief = {
        ...b.state.bs.brief,
        templates: {
          ...(b.state.bs.brief.templates || {})
        }
      };
      if (p.kind === "video") {
        Object.keys(brief.templates).forEach(k => {
          if (k.startsWith("video:") && brief.templates[k] === String(p.row.templateId)) delete brief.templates[k];
        });
        if (t) brief.templates[`video:${String(t.id)}`] = String(t.id);
      } else brief.templates[p.channel] = t ? String(t.id) : null; // null = explicit "no template" (the starter default stays off)
      b.setState({
        plan,
        bs: {
          ...b.state.bs,
          brief
        }
      });
      b.saveState({
        plan,
        brief
      });
      this.setState({
        canvas: {
          ...this.state.canvas,
          [p.key]: null
        },
        edit: null,
        pick: null
      }, () => this.refreshCanvas({
        ...p,
        row: plan.find(r => r.key === p.key)
      }));
    });
    _defineProperty(this, "addPiece", v => {
      const b = this.props.b;
      const plan = (b.state.plan || []).slice();
      const brief = {
        ...b.state.bs.brief,
        templates: {
          ...(b.state.bs.brief.templates || {})
        }
      };
      if (v.startsWith("ch:")) {
        const ch = v.slice(3);
        const t = b.templateFor(brief, ch);
        const have = plan.filter(r => r.kind === "draft" && r.channel === ch).length;
        const key = have ? `d:${ch}:${have + 1}` : `d:${ch}`;
        plan.push({
          key,
          on: true,
          kind: "draft",
          channel: ch,
          piece: b.storyTitle(),
          status: "to write",
          when: new Date().toISOString().slice(0, 10),
          templateId: t ? String(t.id) : null,
          angle: have ? "A different take from the first post: another angle, another visual." : ""
        });
        if (!brief.channels.includes(ch)) brief.channels = [...brief.channels, ch];
      } else {
        const t = b.tpl(v.slice(2));
        if (!t) return;
        brief.templates[`video:${String(t.id)}`] = String(t.id);
        plan.push(b.videoRow(t));
      }
      b.setState({
        plan,
        bs: {
          ...b.state.bs,
          brief
        }
      });
      b.saveState({
        plan,
        brief
      });
      this.setState({
        sel: plan[plan.length - 1].key
      });
    });
    _defineProperty(this, "setAngle", (p, angle) => {
      const b = this.props.b;
      const plan = (b.state.plan || []).map(r => r.key === p.key ? {
        ...r,
        angle
      } : r);
      b.setState({
        plan
      });
      b.saveState({
        plan
      });
    });
    _defineProperty(this, "togglePiece", p => {
      const b = this.props.b;
      const plan = (b.state.plan || []).map(r => r.key === p.key ? {
        ...r,
        on: !r.on
      } : r);
      b.setState({
        plan
      });
      b.saveState({
        plan
      });
    });
    // social / plain text pieces: the repurpose writer (existing rail) and inline edit
    _defineProperty(this, "writeText", async (p, rewrite, opts) => {
      const b = this.props.b;
      const key = `text:${p.key}`;
      this.setBusy(key, true);
      try {
        let extra = opts && opts.guidance || "";
        // A social piece carrying a clip: its transcript guides the caption (Post this clip, or a later Rewrite).
        if (!extra && !OWNED.has(p.channel)) {
          const v0 = this.variantFor(p);
          const pick = v0 ? b.socialMediaFor(v0, this.detailFor(v0)) : null;
          if (pick && pick.videoUrl) {
            const a = await safe(svc("/content/assets").postAsync({
              kind: "video",
              url: pick.videoUrl
            }), null);
            const aid = a && (a.id || a.Id);
            const t = aid ? await safe(svc(`/content/assets/${String(aid)}/transcript`).getAsync(), null) : null;
            const text = t && (t.text || (t.segments || []).map(x => x.text).join(" ")) || "";
            if (text) extra = `This post carries a video. Write the caption from what the video says, in the channel's voice. Transcript: ${text.slice(0, 1500)}`;
          }
        }
        const guidance = {};
        if (p.row.angle || extra) guidance[p.channel] = [p.row.angle, extra, rewrite ? "Write it fresh." : ""].filter(Boolean).join(" ");
        const tmap = p.row.templateId ? `,"templates":{"${p.channel}":${String(p.row.templateId)}}` : "";
        const body = JSON.stringify({
          channels: [p.channel],
          guidance,
          imageUrls: b.storyImages().slice(0, 4),
          rewrite: !!rewrite || pieceOrdinal(p.key) > 1
        }).replace(/}$/, tmap + "}");
        const existing = this.variantFor(p);
        if (existing && rewrite) await safe(svc(`/content/variants/${String(existing.id)}/status`).postAsync({
          status: "archived"
        }), null);
        await svc(`/content/containers/${String(b.state.primary.id)}/repurpose`).postAsync(body);
        toast(`Writing the ${channelLabel(p.channel)} piece: it lands here within a minute.`);
        const before = b.allVariants().filter(v => String(v.channel || "").toLowerCase() === p.channel && String(v.status || "").toLowerCase() !== "archived").length;
        const after = opts && opts.after;
        const knownIds = new Set(b.allVariants().map(v => String(v.id)));
        const poll = async n => {
          if (!this._alive) return;
          await b.reloadDetails();
          const now = b.allVariants().filter(v => String(v.channel || "").toLowerCase() === p.channel && String(v.status || "").toLowerCase() !== "archived").length;
          if (now > before) {
            const fresh = b.allVariants().find(v => String(v.channel || "").toLowerCase() === p.channel && !knownIds.has(String(v.id)));
            if (fresh && pieceOrdinal(p.key) > 1) {
              await this.savePieceMap({
                ...this.pieceMap(),
                [p.key]: String(fresh.id)
              });
              await b.reloadDetails();
            }
            if (after) {
              try {
                await after(fresh || this.variantFor(p));
              } catch (e) {/* best effort */}
            }
          }
          if (now <= before && n < 18) setTimeout(() => poll(n + 1), 8000);else {
            this.setBusy(key, false);
            if (now > before) toast("The piece is in.");
          }
        };
        setTimeout(() => poll(0), 8000);
      } catch (e) {
        this.setBusy(key, false);
        toast(e && e.message || "Could not start the writer.");
      }
    });
    _defineProperty(this, "approveVideo", async (p, vv) => {
      const b = this.props.b;
      if (vv) {
        await b.setVariantStatus(vv, "approved");
        return;
      }
      const out = b.state.mediaOut[p.row.key];
      if (!out) return;
      const vid = await b.useExistingVideo(p.row, {
        url: out,
        title: this.pieceTitle(p)
      }, {
        quiet: true
      });
      const v = vid && b.allVariants().find(x => String(x.id) === vid);
      if (v) {
        await b.setVariantStatus(v, "approved");
        toast(`${this.pieceTitle(p)} approved.`);
      }
    });
    _defineProperty(this, "closeMenus", () => this.setState({
      addOpen: false,
      rowMenu: null
    }));
    _defineProperty(this, "addAndSelect", v => {
      this.closeMenus();
      this.addPiece(v);
      setTimeout(() => {
        if (!this._alive) return;
        const p = this.pieces().find(x => x.key === this.state.sel);
        if (p) this.select(p);
      }, 0);
    });
    _defineProperty(this, "writeAll", async () => {
      this.setState({
        allBusy: true
      });
      try {
        await this.makeEverything();
      } finally {
        if (this._alive) this.setState({
          allBusy: false
        });
      }
    });
    // The video editors take an asset record (id, url, duration); a slot only knows the URL, so register or look it up first.
    _defineProperty(this, "openMedia", async m => {
      if (m.kind === "edit" || m.kind === "resize" || m.url && typeof m.url === "object") {
        this.setState({
          mediaEdit: m
        });
        return;
      }
      const a = await safe(svc("/content/assets").postAsync({
        kind: "video",
        url: m.url
      }), null);
      if (!a || !(a.id || a.Id)) {
        toast("Could not open this video.");
        return;
      }
      let durationSeconds = Number(a.durationSeconds) || 0;
      if (!durationSeconds) {
        // the file was registered without a duration: read it from the media itself
        durationSeconds = await new Promise(resolve => {
          const v = document.createElement("video");
          v.preload = "metadata";
          v.muted = true;
          const done = d => {
            v.src = "";
            resolve(d);
          };
          v.onloadedmetadata = () => done(Math.round(v.duration || 0));
          v.onerror = () => done(0);
          setTimeout(() => done(0), 8000);
          v.src = a.url || m.url;
        });
      }
      this.setState({
        mediaEdit: {
          ...m,
          asset: {
            ...a,
            id: a.id || a.Id,
            url: a.url || m.url,
            durationSeconds
          }
        }
      });
    });
    _defineProperty(this, "makeEverything", async () => {
      const b = this.props.b;
      for (const p of this.pieces().filter(x => x.on)) {
        if (p.kind === "video") continue;
        const v = this.variantFor(p);
        const d = this.designFor(p, v);
        if (d && OWNED.has(p.channel)) {
          const empty = this.emptySlots(d);
          if (empty.length) await this.fill(p, empty);
        } else if (!v) await this.writeText(p, false);
      }
      b.reloadDetails();
    });
    _defineProperty(this, "commentOnQuote", (p, v) => {
      const id = String(v.id);
      const q = this.state.quote.text;
      const start = plainBody(v.body).replace(/\s+/g, " ").indexOf(q);
      this.setState({
        quote: null,
        noteAnchor: {
          ...(this.state.noteAnchor || {}),
          [id]: {
            quote: q,
            start: start >= 0 ? start : null,
            end: start >= 0 ? start + q.length : null
          }
        },
        open: {
          ...(this.state.open || {}),
          [`cmt:${id}`]: true
        }
      }, () => setTimeout(() => {
        const el = document.getElementById(`bm-note-${id}`);
        if (el) {
          el.scrollIntoView({
            block: "nearest"
          });
          el.focus();
        }
      }, 0));
    });
    _defineProperty(this, "toggleSection", (k, dflt) => this.setState({
      open: {
        ...(this.state.open || {}),
        [k]: !this.sectionOpen(k, dflt)
      }
    }));
    _defineProperty(this, "loadAssetIds", async () => {
      const b = this.props.b;
      const sid = String(b.state.storyId || b.state.primary && (b.state.primary.storyId || b.state.primary.id) || "");
      if (!sid) return;
      const r = await safe(svc(`/content/stories/${sid}/assets?$top=200`).getAsync(), null);
      const map = {
        ...(this.state.assetIds || {})
      };
      (r && r.items || []).forEach(a => {
        if (a && a.url && a.id) map[a.url] = String(a.id);
      });
      if (this._alive) this.setState({
        assetIds: map
      });
    });
    _defineProperty(this, "openImageEditor", id => {
      const b = this.props.b;
      const sid = String(b.state.storyId || b.state.primary && b.state.primary.id || "");
      b.props.history.push(`/Content/images/${id}?back=${encodeURIComponent(`/Content/build/${sid}`)}`);
    });
    _defineProperty(this, "rewrite", async p => {
      const v = this.variantFor(p);
      const d = this.designFor(p, v);
      const note = String((this.state.ask || {})[p.key] || "").trim();
      if (OWNED.has(p.channel) && d) await this.fill(p, null, note);else await this.writeText(p, !!v, note ? {
        guidance: note
      } : undefined);
      if (this._alive) this.setState({
        ask: {
          ...(this.state.ask || {}),
          [p.key]: ""
        }
      });
    });
    _defineProperty(this, "runBrandCheck", async v => {
      const b = this.props.b;
      const id = String(v.id);
      this.setState({
        checking: id
      });
      try {
        const res = await b.safetyCheck([{
          key: id,
          v
        }]);
        if (this._alive) this.setState({
          checks: {
            ...(this.state.checks || {}),
            [id]: {
              s: res[id],
              body: v.body,
              title: v.title
            }
          }
        });
      } finally {
        if (this._alive) this.setState({
          checking: null
        });
      }
    });
    // ---- In-place editing on the canvas (DS 2026-10-02): hover a block for its actions, click text to edit, click an image to pick.
    _defineProperty(this, "wireCanvas", (p, iframe) => {
      const doc = iframe && iframe.contentDocument;
      if (!doc || !doc.body || doc.__plkWired) return;
      doc.__plkWired = true;
      const style = doc.createElement("style");
      style.textContent = "[data-slot]{position:relative;transition:outline-color .12s}[data-slot]:hover{outline:2px dashed #ff6358;outline-offset:4px;border-radius:8px}[data-field]{cursor:text}[data-field=\"image\"]{cursor:pointer}[data-field]:hover{outline:1px dotted rgba(255,99,88,.8);outline-offset:2px;border-radius:4px}[data-field][contenteditable=\"true\"]{outline:2px solid #ff6358;outline-offset:2px;border-radius:4px;background:rgba(255,99,88,.05)}";
      doc.head.appendChild(style);
      const place = el => {
        const fr = iframe.getBoundingClientRect();
        const r = el.getBoundingClientRect();
        const stage = iframe.parentElement.getBoundingClientRect();
        return {
          top: fr.top - stage.top + r.top,
          left: fr.left - stage.left + r.left,
          width: r.width,
          height: r.height
        };
      };
      doc.addEventListener("mouseover", e => {
        const slot = e.target.closest && e.target.closest("[data-slot]");
        if (!slot) return;
        const i = Number(slot.getAttribute("data-slot"));
        const h = this.state.hover;
        if (h && h.key === p.key && h.i === i && !h.pin) return;
        if (h && h.pin) return;
        clearTimeout(this._hoverOff);
        this.setState({
          hover: {
            key: p.key,
            i,
            type: slot.getAttribute("data-type"),
            ...place(slot)
          }
        });
      });
      doc.addEventListener("mouseleave", () => {
        clearTimeout(this._hoverOff);
        this._hoverOff = setTimeout(() => {
          if (this._alive && this.state.hover && !this.state.hover.pin && !this._overBar) this.setState({
            hover: null
          });
        }, 220);
      });
      doc.addEventListener("scroll", () => {
        if (this.state.hover && !this.state.hover.pin) this.setState({
          hover: null
        });
      }, true);
      doc.addEventListener("click", e => {
        const a = e.target.closest && e.target.closest("a");
        if (a) e.preventDefault();
        if (this.pickQuote(p, doc.getSelection())) return; // selected words: offer "Comment on these words" (phase 3)
        const el = e.target.closest && e.target.closest("[data-field]");
        if (!el) return;
        const slot = el.closest("[data-slot]");
        const i = slot ? Number(slot.getAttribute("data-slot")) : -1;
        if (i < 0) return;
        const field = el.getAttribute("data-field");
        if (field === "image") {
          this.openPop(p, i, "pick", {
            key: p.key,
            i,
            ...place(slot)
          });
          return;
        }
        if (el.isContentEditable) return;
        this.startInline(p, i, field, el);
      });
    });
    _defineProperty(this, "startInline", (p, i, field, el) => {
      const orig = field === "html" ? el.innerHTML : el.textContent;
      el.setAttribute("contenteditable", "true");
      el.focus();
      try {
        const r = el.ownerDocument.createRange();
        r.selectNodeContents(el);
        r.collapse(false);
        const sel = el.ownerDocument.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
      } catch (e) {/* caret */}
      this.setState({
        hover: {
          ...(this.state.hover || {
            key: p.key,
            i
          }),
          pin: true
        }
      });
      const finish = async cancel => {
        el.removeEventListener("blur", onBlur);
        el.removeEventListener("keydown", onKey);
        el.removeAttribute("contenteditable");
        const value = field === "html" ? el.innerHTML : el.textContent.trim();
        if (cancel) {
          if (field === "html") el.innerHTML = orig;else el.textContent = orig;
        }
        this.setState({
          hover: null
        });
        if (!cancel && value !== (field === "html" ? orig : orig.trim())) await this.saveSlot(p, i, {
          [field]: value
        });
      };
      const onBlur = () => finish(false);
      const onKey = e => {
        if (e.key === "Escape") {
          e.preventDefault();
          finish(true);
        } else if (e.key === "Enter" && field !== "html" && !e.shiftKey) {
          e.preventDefault();
          el.blur();
        }
      };
      el.addEventListener("blur", onBlur);
      el.addEventListener("keydown", onKey);
    });
    _defineProperty(this, "previewTemplate", async (p, t, cur) => {
      const b = this.props.b;
      const td = t.design ? parseJson(t.design) : null;
      if (!td || !Array.isArray(td.sections)) {
        this.setTemplate(p, String(t.id), {
          apply: true
        });
        return;
      }
      const design = this.mapDesign(cur, td);
      this.setState({
        tplPreview: {
          key: p.key,
          t,
          design,
          html: null
        }
      });
      const r = await safe(svc("/content/templates/render").postAsync(JSON.stringify({
        design,
        mode: "final"
      }).replace(/}$/, `,"containerId":${String(b.state.primary.id)}}`)), null);
      if (this._alive && this.state.tplPreview && this.state.tplPreview.key === p.key) this.setState({
        tplPreview: {
          ...this.state.tplPreview,
          html: r && r.html || "",
          body: r && r.body
        }
      });
    });
    _defineProperty(this, "applyTemplatePreview", async () => {
      const tp = this.state.tplPreview;
      if (!tp) return;
      const p = this.pieces().find(x => x.key === tp.key);
      if (!p) return;
      this.setState({
        tplPreview: null
      });
      this.setTemplate(p, String(tp.t.id), {
        apply: true
      });
      await this.persist(p, tp.design, tp.html, tp.body);
      toast(`Now on ${humanName(tp.t.name)}. Empty parts can be written from the story.`);
    });
    /** A panel on the canvas next to the block: the field form, the image picker or the Generate options. at = the hover rect (or null for the top). */
    _defineProperty(this, "openPop", (p, i, kind, at) => {
      const stage = this._iframes[p.key] && this._iframes[p.key].parentElement;
      const w = stage ? stage.clientWidth : 800;
      const hgt = stage ? stage.clientHeight : 900;
      // keep the panel inside the stage (the frame clips): a block near the bottom gets the panel above its toolbar
      const popAt = at && at.top != null ? {
        top: Math.max(2, Math.min(at.top + 4, hgt - 340)),
        left: Math.max(14, Math.min(at.left, w - 400))
      } : {
        top: 8,
        left: 14
      };
      const d = this.designFor(p, this.variantFor(p));
      const s = d && d.sections[i] ? d.sections[i] : {};
      const patch = {
        edit: null,
        pick: null,
        gen: null,
        popAt,
        hover: null
      };
      if (kind === "edit") patch.edit = {
        key: p.key,
        i,
        form: {
          headline: s.headline || "",
          subhead: s.subhead || "",
          html: s.html || "",
          label: s.label || "",
          url: s.url || "",
          items: (s.items || []).join("\n")
        }
      };
      if (kind === "pick") patch.pick = {
        key: p.key,
        i
      };
      if (kind === "gen") patch.gen = {
        key: p.key,
        i,
        kind: "hero",
        direction: ""
      };
      this.setState(patch);
    });
    _defineProperty(this, "closePop", () => this.setState({
      edit: null,
      pick: null,
      gen: null
    }));
    // An existing video from Assets fills the piece the way a made one does (2026-10-16): it joins the story, then mediaOut.
    _defineProperty(this, "useAssetVideo", async (r, x) => {
      if (!x || x.kind !== "video") {
        toast("Pick a video for this piece.");
        return;
      }
      this.setState({
        vidPick: null
      });
      await this.props.b.useExistingVideo(r, x);
    });
    _defineProperty(this, "scrollCanvasTo", (p, i) => {
      const f = this._iframes[p.key];
      const doc = f && f.contentDocument;
      const el = doc && doc.querySelector(`[data-slot="${i}"]`);
      if (el) el.scrollIntoView({
        block: "center",
        behavior: "smooth"
      });
    });
    _defineProperty(this, "allFormats", async (r, out) => {
      const b = this.props.b;
      const todo = this.missingFormats(out);
      if (!todo.length) {
        toast("Every size is already made.");
        return;
      }
      let ok = 0;
      const failed = [];
      for (const a of todo) {
        const url = await b.reframe(out, r.key, a);
        if (url) ok++;else failed.push(a);
      }
      toast(failed.length ? `${ok} of ${todo.length} sizes made; ${failed.join(", ")} did not come out. Try again for the rest.` : `${ok} size${ok === 1 ? "" : "s"} made: each is saved under this story, ready to publish.`);
    });
    _defineProperty(this, "postClip", async (out, pieceKey) => {
      const b = this.props.b;
      const p = this.pieces().find(x => x.key === pieceKey);
      if (!p) return;
      const cid = String(b.state.primary.id);
      const a = await safe(svc("/content/assets").postAsync({
        kind: "video",
        url: out
      }), null);
      const aid = a && (a.id || a.Id);
      const t = aid ? await safe(svc(`/content/assets/${String(aid)}/transcript`).getAsync(), null) : null;
      const text = t && (t.text || (t.segments || []).map(x => x.text).join(" ")) || "";
      if (!text) {
        await safe(svc(`/content/containers/${cid}/transcribe`).postAsync({
          videoUrl: out
        }), null);
      }
      const guidance = text ? `This post carries a video. Write the caption from what the video says, in the channel's voice. Transcript: ${text.slice(0, 1500)}` : "This post carries a video of the product in motion; write the caption to make people watch it.";
      const v = this.variantFor(p);
      const attach = async vv => {
        if (vv) await b.setSocialMedia(vv, {
          videoUrl: out,
          imageUrl: null
        });
      };
      if (v && !text) {
        // keep the caption that exists; the transcript is on its way, Rewrite later pulls it in
        await attach(v);
        this.select(p);
        toast(`Clip attached to ${p.label}. No transcript yet: one is being made; Rewrite the caption in a minute to write it from the video.`);
        return;
      }
      await this.writeText(p, !!v, {
        guidance,
        after: attach
      });
      this.select(p);
      toast(v ? `Clip attached to ${p.label}; its caption is being rewritten from the transcript.` : `${p.label} is being written from the ${text ? "transcript" : "story"}; the clip attaches when it lands.`);
    });
    // ---- Ad piece → flight builder (DS 2026-10-02): the builder reads this on mount and fills ad copy + creative.
    _defineProperty(this, "sendToFlight", (p, v) => {
      const b = this.props.b;
      const prefill = b.flightPrefill(v, this.detailFor(v));
      try {
        sessionStorage.setItem("plunk-flight-prefill", JSON.stringify(prefill));
      } catch (e) {/* private mode */}
      b.props.history.push("/Campaigns/FlightBuilder");
    });
    // ---- Landing / blog export (DS 2026-10-02: the page lives on the website; preview + export, no hosting)
    _defineProperty(this, "exportPage", async (p, v, how) => {
      const b = this.props.b;
      const canvas = this.state.canvas[p.key];
      const html = canvas && canvas.html && /<html/i.test(canvas.html) ? canvas.html.replace(/ data-(slot|type|field)="[^"]*"/g, "") : /<html/i.test(v.body || "") ? v.body : `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${(v.title || "").replace(/</g, "&lt;")}</title></head><body>${v.body || ""}</body></html>`;
      const fr = PAGE_CHANNELS.has(p.channel) && this.state.frameOn !== false ? this.frameData() : null;
      const framed = fr ? wrapFrame(html, fr, false) : html;
      const fileBase = (v.title || b.storyTitle() || "page").replace(/[^\w\s-]+/g, "").trim().replace(/\s+/g, "-").toLowerCase() || "page";
      if (how === "html") {
        const blob = new Blob([canvas && canvas.body || v.body || html], {
          type: "text/html"
        });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${fileBase}.html`;
        a.click();
        toast("HTML downloaded: the page body, ready to paste into the website.");
        return;
      }
      if (how === "open" || how === "pdf") {
        const w = window.open("", "_blank");
        if (!w) {
          toast("Pop-up blocked: allow pop-ups for this site.");
          return;
        }
        w.document.open();
        w.document.write(framed);
        w.document.close();
        if (how === "pdf") setTimeout(() => {
          try {
            w.focus();
            w.print();
          } catch (e) {/* user prints */}
        }, 600);
        return;
      }
      if (how === "png") {
        toast("Making a picture of the page: under a minute.");
        try {
          const r = await runAgent("content_capture_agent", "Capture this page", {
            html: framed,
            maxSections: "0",
            width: "1200"
          });
          const done = await waitAgent(runIdOf(r), 5000, 30, () => this._alive);
          const url = done.ok && (done.ctx && done.ctx.url || done.res && done.res.url);
          if (!url) {
            toast(done.timeout ? "Still rendering: it will show up in Assets." : "The page image could not be made.");
            return;
          }
          await safe(svc("/content/assets").postAsync({
            kind: "image",
            url,
            title: `Page image · ${v.title || b.storyTitle()}`,
            source: "capture"
          }), null);
          window.open(url, "_blank", "noopener");
          toast("Page image saved to Assets and opened.");
        } catch (e) {
          toast(e && e.message || "Could not make the page picture.");
        }
      }
    });
    _defineProperty(this, "useImageOn", async (url, pieceKey) => {
      const p = this.pieces().find(x => x.key === pieceKey);
      if (!p || !url) return;
      const b = this.props.b;
      const v = this.variantFor(p);
      const d = this.designFor(p, v);
      if (d && OWNED.has(p.channel)) {
        const i = d.sections.findIndex(sec => IMAGE_TYPES.has(String(sec.type || "").toLowerCase()));
        if (i < 0) {
          toast("That piece's layout has no place for a picture.");
          return;
        }
        const sec = d.sections[i];
        await this.saveSlot(p, i, String(sec.type).toLowerCase() === "gallery" ? {
          images: [...(sec.images || []), url].slice(-3)
        } : {
          imageUrl: url,
          imageFrom: "url"
        });
      } else {
        if (!v) {
          toast("Write that piece first, then use the image on it.");
          return;
        }
        await b.setSocialMedia(v, {
          imageUrl: url
        });
      }
      this.select(p);
      toast(`Picture placed on ${p.label}.`);
    });
  }
  componentDidMount() {
    this._alive = true;
    this.ensurePlan();
    this.loadAssetIds();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  componentDidUpdate() {
    const p = this.pieces();
    // a bell link (?piece=) lands on that piece with its notes open (phase 3)
    const deep = this.props.b._deepPiece;
    if (deep && p.length) {
      const hit = p.find(x => {
        const v = this.variantFor(x);
        return v && String(v.id) === String(deep);
      });
      this.props.b._deepPiece = null;
      if (hit) {
        this.setState({
          open: {
            ...(this.state.open || {}),
            [`cmt:${deep}`]: true
          }
        });
        this.select(hit);
        return;
      }
    }
    if (!this.state.sel && p.length) this.select(p[0]);
    // presence: the selected piece, and whether its editor is open (Builder dedupes and heartbeats)
    const sel = p.find(x => x.key === this.state.sel);
    const v = sel ? this.variantFor(sel) : null;
    if (this.props.b.setPresence) this.props.b.setPresence(v ? v.id : null, !!(this.state.edit && sel && this.state.edit.key === sel.key));
  }

  // ---------------------------------------------------------------- pieces
  async ensurePlan() {
    const b = this.props.b;
    if (!b.state.plan && !b.state.busy.plan) await b.planIt({
      stay: true
    });
    const p = this.pieces();
    if (p.length && !this.state.sel) this.select(p[0]);
  }
  pieces() {
    const b = this.props.b;
    const plan = b.state.plan || [];
    const drafts = plan.filter(r => r.kind === "draft").map(r => {
      const n = pieceOrdinal(r.key);
      return {
        key: r.key,
        kind: "draft",
        channel: r.channel,
        n,
        label: n > 1 ? `${channelLabel(r.channel)} · ${n}` : `${channelLabel(r.channel)}`,
        row: r,
        on: r.on
      };
    });
    const videos = plan.filter(r => r.kind === "media" && /^video|^cinematic/.test(r.media)).map(r => ({
      key: r.key,
      kind: "video",
      channel: "video",
      label: r.label,
      row: r,
      on: r.on
    }));
    return drafts.concat(videos);
  }
  /** piece_map block on the primary piece: { "d:social_instagram:2": "<variantId>" }: which variant belongs to a second piece of a channel. */
  pieceMap() {
    const b = this.props.b;
    const d = b.state.details[String(b.state.primary.id)] || {};
    const blk = latestBlock(d.blocks, "piece_map");
    return blk && parseJson(blk.body) || {};
  }
  variantFor(p) {
    const b = this.props.b;
    if (p.kind === "video") return null;
    const same = b.allVariants().filter(v => String(v.channel || "").toLowerCase() === p.channel);
    const live = same.filter(v => String(v.status || "").toLowerCase() !== "archived");
    const map = this.pieceMap();
    const taken = new Set(Object.values(map).map(String));
    if (map[p.key]) {
      const hit = same.find(v => String(v.id) === String(map[p.key]));
      if (hit) return hit;
    }
    // unmapped: the first piece takes the first live variant not claimed by another piece; a later piece takes the nth
    const free = live.filter(v => !taken.has(String(v.id)));
    const n = pieceOrdinal(p.key);
    const mine = free[n - 1] || (n === 1 ? same.find(v => !taken.has(String(v.id))) || null : null);
    return mine;
  }
  detailFor(v) {
    const b = this.props.b;
    return v ? b.state.details[String(v.piece ? v.piece.id : v.containerId)] || {} : b.state.details[String(b.state.primary.id)] || {};
  }
  templateFor(p) {
    const b = this.props.b;
    return p.row && p.row.templateId ? b.tpl(p.row.templateId) : null;
  }
  designFor(p, v) {
    // the filled design saved on the piece wins; else the template's design with empty slots
    const w = v ? parseJson((latestBlock(this.detailFor(v).blocks, "design_filled", forVariant(v)) || {}).body) : null;
    const d = w && w.design ? w.design : w;
    if (d && Array.isArray(d.sections)) return d;
    const t = this.templateFor(p);
    const td = t && t.design ? parseJson(t.design) : null;
    return td && Array.isArray(td.sections) ? td : null;
  }
  setBusy(k, v) {
    this.setState({
      busy: {
        ...this.state.busy,
        [k]: v
      }
    });
  }

  // ---------------------------------------------------------------- site frame (DS 2026-10-02)
  siteFrame() {
    const b = this.props.b;
    const d = b.state.details[String(b.state.primary.id)] || {};
    const blk = latestBlock(d.blocks, "site_frame");
    const f = blk && parseJson(blk.body);
    return f && f.frameUrl && !f.removed ? f : null;
  }
  frameData() {
    const f = this.siteFrame();
    if (!f) return null;
    const got = this.state.frames[f.frameUrl];
    if (got === undefined) this.loadFrame(f.frameUrl);
    return got || null;
  }
  loadFrame(url) {
    if (this._frameLoading && this._frameLoading[url]) return;
    this._frameLoading = this._frameLoading || {};
    this._frameLoading[url] = true;
    fetch(url).then(r => r.ok ? r.json() : null).then(j => {
      if (this._alive) this.setState({
        frames: {
          ...this.state.frames,
          [url]: j || null
        }
      });
    }).catch(() => {
      if (this._alive) this.setState({
        frames: {
          ...this.state.frames,
          [url]: null
        }
      });
    });
  }
  firstSourceUrl() {
    const b = this.props.b;
    const d = b.state.details[String(b.state.primary.id)] || {};
    const blk = latestBlock(d.blocks, "source_url");
    const src = blk && String(blk.body || "").trim() || "";
    if (src) return src;
    const shop = b.state.shopify && b.state.shopify.shop;
    return shop ? `https://${String(shop).replace(/^https?:\/\//, "").replace(/\/.*$/, "")}/` : "";
  }
  renderFrameRow(p) {
    const fe = this.state.frameEdit;
    if (!fe || !PAGE_CHANNELS.has(p.channel)) return null;
    const fr = this.siteFrame();
    const ok = /^https?:\/\//i.test(fe.url || "");
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-make-angle hub-make-siteframe"
    }, /*#__PURE__*/React.createElement("span", {
      className: "k"
    }, "Website frame"), /*#__PURE__*/React.createElement("input", {
      value: fe.url || "",
      placeholder: "https://your-site.com/",
      disabled: !!fe.busy,
      onChange: e => this.setState({
        frameEdit: {
          ...fe,
          url: e.target.value
        }
      }),
      onKeyDown: e => {
        if (e.key === "Enter" && ok && !fe.busy) this.captureFrame(fe.url);
      }
    }), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle sm",
      style: {
        margin: 0
      },
      disabled: !ok || !!fe.busy,
      onClick: () => this.captureFrame(fe.url)
    }, fe.busy ? "Reading…" : fr ? "Read again" : "Read the site"), fr && /*#__PURE__*/React.createElement("label", {
      className: "hub-create-check",
      style: {
        margin: 0
      }
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      className: "hub-cb",
      checked: this.state.frameOn !== false,
      onChange: () => this.setState({
        frameOn: this.state.frameOn === false
      })
    }), /*#__PURE__*/React.createElement("span", null, "Show in the preview")), fr && /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle sm",
      style: {
        margin: 0
      },
      disabled: !!fe.busy,
      onClick: this.removeFrame
    }, "Remove"), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, fr ? `Header, nav and footer from ${fr.host}: the preview, Open as a page, PDF and the page image use them.` : "Reads the header, nav and footer from that page and puts this one inside them."));
  }
  // ---------------------------------------------------------------- canvas
  async refreshCanvas(p) {
    const b = this.props.b;
    const v = this.variantFor(p);
    const d = this.designFor(p, v);
    if (!d || !OWNED.has(p.channel)) return;
    const cur = this.state.canvas[p.key];
    if (cur && cur.design === d) return;
    if (cur === null) {/* cleared on a template change: render again */}
    const filledAny = d.sections.some(s => s.headline || s.html || s.items && s.items.length);
    // "canvas" = the final render with data-slot / data-field markers (in-place editing); never what persist() stores.
    const body = JSON.stringify({
      design: d,
      mode: filledAny ? "canvas" : "placeholder"
    }).replace(/}$/, `,"containerId":${String(b.state.primary.id)}}`);
    const r = await safe(svc("/content/templates/render").postAsync(body), null);
    if (this._alive && r && r.html) this.setState({
      canvas: {
        ...this.state.canvas,
        [p.key]: {
          html: r.html,
          body: r.body,
          design: d
        }
      }
    });
  }
  async markCanvas(p, design) {
    const b = this.props.b;
    const r = await safe(svc("/content/templates/render").postAsync(JSON.stringify({
      design,
      mode: "canvas"
    }).replace(/}$/, `,"containerId":${String(b.state.primary.id)}}`)), null);
    if (this._alive && r && r.html) {
      const cur = this.state.canvas[p.key];
      if (cur && cur.design === design) this.setState({
        canvas: {
          ...this.state.canvas,
          [p.key]: {
            ...cur,
            html: r.html
          }
        }
      });
    }
  }
  async persist(p, design, html, pageBody, titleHint) {
    // the variant body is the render (HTML for owned channels); the filled design rides on a block for re-editing
    const b = this.props.b;
    const cid = String(b.state.primary.id);
    let v = this.variantFor(p);
    const bodyHtml = p.channel === "email" ? html : pageBody || html;
    const title = titleHint || v && v.title || (design.sections.find(s => s.headline) || {}).headline || b.storyTitle();
    if (!v) {
      const created = await safe(svc(`/content/containers/${cid}/variants`).postAsync({
        channel: p.channel,
        title,
        body: bodyHtml,
        status: "draft"
      }), null);
      await b.reloadDetails();
      v = this.variantFor(p);
      if (!v && created && (created.id || created.Id)) v = {
        id: created.id || created.Id,
        containerId: cid
      };
      if (v && p.row && p.row.templateId) b.markUsed(p.row.templateId, `variantId=${String(v.id)}`);
    } else {
      if (!(await b.saveText(v, {
        title,
        body: bodyHtml
      }))) {
        await b.reloadDetails();
        return;
      }
    }
    if (v) await safe(svc(`/content/containers/${cid}/blocks`).postAsync({
      kind: "design_filled",
      body: JSON.stringify({
        variantId: String(v.id),
        design
      })
    }), null);
    await b.reloadDetails();
    this.setState({
      canvas: {
        ...this.state.canvas,
        [p.key]: {
          html,
          body: pageBody,
          design
        }
      },
      hover: null
    }, () => this.markCanvas(p, design));
  }
  emptySlots(d) {
    return d.sections.map((s, i) => [s, i]).filter(([s]) => TEXT_TYPES.has(String(s.type || "").toLowerCase()) && !(s.headline || s.html || s.items && s.items.length || String(s.type).toLowerCase() === "button" && s.label)).map(([, i]) => i);
  }
  /** A story-pool thumbnail in a Pick image grid: click uses it here, hover offers Use on another piece. */
  pickItem(u, onPick) {
    const drafts = this.pieces().filter(x => x.kind === "draft" && x.key !== this.state.sel);
    return /*#__PURE__*/React.createElement("span", {
      key: u,
      className: "hub-make-pickitem"
    }, /*#__PURE__*/React.createElement("img", {
      src: u,
      alt: "",
      loading: "lazy",
      onClick: onPick
    }), drafts.length > 0 && /*#__PURE__*/React.createElement("select", {
      className: "hub-make-useon",
      value: "",
      title: "Use this image on another piece",
      onClick: e => e.stopPropagation(),
      onChange: e => {
        const k = e.target.value;
        if (k) this.useImageOn(u, k);
      }
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "Use on\u2026"), drafts.map(x => /*#__PURE__*/React.createElement("option", {
      key: x.key,
      value: x.key
    }, x.label))));
  }
  genBusy(p, i) {
    const pre = `slot:${p.key}:${i}:`;
    return Object.keys(this.props.b.state.busy || {}).some(k => k.startsWith(pre) && this.props.b.state.busy[k]);
  }
  renderGenPopover(p, i) {
    const g = this.state.gen;
    if (!g || g.key !== p.key || g.i !== i) return null;
    const t = this.templateFor(p);
    const media = t ? recipeOf(t).media : {};
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-make-gen pop"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-make-gen-kinds"
    }, GEN_KINDS.map(([k, l, n]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      className: "territory-toggle xs" + (g.kind === k ? " active" : ""),
      onClick: () => this.setState({
        gen: {
          ...g,
          kind: k
        }
      })
    }, l, n > 1 ? ` (${n})` : ""))), /*#__PURE__*/React.createElement("input", {
      value: g.direction,
      placeholder: "What to show (optional): the setting, the mood, the angle",
      onChange: e => this.setState({
        gen: {
          ...g,
          direction: e.target.value
        }
      }),
      style: {
        ...INPUT,
        marginTop: 6
      }
    }), /*#__PURE__*/React.createElement("div", {
      className: "hub-make-gen-foot"
    }, /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "Starts from the story's product picture", g.kind === "hero" ? "" : ". The first one goes on the piece; the rest wait under Pick a picture", "."), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary",
      onClick: () => this.generateImage(p, i, g)
    }, "Generate")));
  }
  // ---------------------------------------------------------------- render
  // Make, three columns (DS 2026-10-08 mockup Make.dc.html): pieces on the left, the piece as it will look on a grey
  // stage in the middle, and what to do with it on the right (approve, ask for changes, picture, extras, checks).
  /** Honest per-piece state from the data: skipped, in progress, approved/published, written (review) or not written. */
  rowStatus(p) {
    const b = this.props.b;
    if (!p.on) return "skipped";
    if (p.kind === "video") {
      const k = p.row.key;
      const bz = b.state.busy || {};
      if (bz[`sb:${k}`] || bz[`rv:${k}`] || bz[`clip:${k}`] || bz[`rf:${k}`]) return "making";
      if (!b.state.mediaOut[k]) return "unmade";
      const vv = this.videoVariantFor(p);
      const vs = String(vv && vv.status || "").toLowerCase();
      return vs === "published" || vs === "live" ? "published" : vs === "approved" ? "approved" : "ready";
    }
    if (this.state.busy[`fill:${p.key}`] || this.state.busy[`slot:${p.key}`] || this.state.busy[`text:${p.key}`]) return "writing";
    const v = this.variantFor(p);
    if (!v) return "empty";
    const st = String(v.status || "").toLowerCase();
    if (st === "published" || st === "live") return "published";
    if (st === "approved") return "approved";
    const rs = this.props.b.reviewsOn() ? this.props.b.reviewState(v) : null; // phase 4: where the piece is in its review
    return rs ? `rv_${rs}` : "review";
  }
  /** The newest video variant behind a video row's current video (made or picked from Assets). */
  videoVariantFor(p) {
    const b = this.props.b;
    const out = b.state.mediaOut[p.row.key];
    if (!out) return null;
    const vids = b.renderedVideos();
    const own = vids.filter(x => x.rowKey === p.row.key);
    if (own.length) return own[own.length - 1].v;
    // older videos carry no row: match by file only when no other row uses the same file (else every row would share one version)
    // an older untagged version belongs to the first row (plan order) that uses its file; other rows get their own on Approve
    const firstRow = (b.state.plan || []).find(r => r.kind === "media" && b.state.mediaOut[r.key] === out);
    if (firstRow && firstRow.key !== p.row.key) return null;
    const hits = vids.filter(x => x.url === out && !x.rowKey);
    return hits.length ? hits[hits.length - 1].v : null;
  }
  approvedCount(pieces) {
    return pieces.filter(p => p.on && p.kind !== "video" && ["approved", "published"].includes(this.rowStatus(p))).length;
  }
  pieceTitle(p) {
    if (p.kind === "video") return humanName(p.label) || "Video";
    const v = this.variantFor(p);
    const t = v ? strip(v.title) : "";
    return t || `${kindOf(p)[0]}${p.n > 1 ? ` ${p.n}` : ""}`;
  }
  render() {
    const b = this.props.b;
    const pieces = this.pieces();
    const sel = pieces.find(p => p.key === this.state.sel) || pieces[0];
    if (b.state.busy.plan && !b.state.plan) return /*#__PURE__*/React.createElement("div", {
      className: "bm-view"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bm-loading"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons bm-spin"
    }, "autorenew"), "Plunk is reading the story and planning the pieces\u2026"));
    return /*#__PURE__*/React.createElement("div", {
      className: "bm-view"
    }, (this.state.addOpen || this.state.rowMenu) && /*#__PURE__*/React.createElement("div", {
      className: "bm-scrim",
      onClick: this.closeMenus
    }), /*#__PURE__*/React.createElement("div", {
      className: "bm-cols"
    }, this.renderPieceList(pieces, sel), /*#__PURE__*/React.createElement("div", {
      className: "bm-stage"
    }, sel ? this.renderStage(sel) : /*#__PURE__*/React.createElement("div", {
      className: "bm-empty"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "post_add"), "No pieces yet. Add one, or go back to the Brief and pick where this story goes.")), /*#__PURE__*/React.createElement("aside", {
      className: "bm-panel",
      "aria-label": "This piece"
    }, sel ? this.renderPanel(sel) : null)), this.renderExtras());
  }
  renderAddMenu() {
    const b = this.props.b;
    const plan = b.state.plan || [];
    const vids = b.state.templates.filter(t => {
      const mk = mediaRowFor(t);
      return mk && /^video|^cinematic/.test(mk) && !plan.some(r => String(r.templateId) === String(t.id) && r.kind === "media");
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "bm-menu bm-addmenu",
      role: "menu"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bm-menu-h"
    }, "Written pieces"), CHANNEL_KEYS.map(k => {
      const n = plan.filter(r => r.kind === "draft" && r.channel === k).length;
      return /*#__PURE__*/React.createElement("button", {
        key: k,
        role: "menuitem",
        onClick: () => this.addAndSelect(`ch:${k}`)
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, CHANNEL_ICON[k] || "description"), (KIND[k] || [channelLabel(k)])[0], n ? /*#__PURE__*/React.createElement("i", null, "another one") : null);
    }), vids.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "bm-menu-h"
    }, "Videos"), vids.map(t => /*#__PURE__*/React.createElement("button", {
      key: String(t.id),
      role: "menuitem",
      onClick: () => this.addAndSelect(`v:${String(t.id)}`)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "movie"), humanName(t.name))));
  }
  renderPieceList(pieces, sel) {
    const b = this.props.b;
    const on = pieces.filter(p => p.on);
    const approved = this.approvedCount(pieces);
    const total = on.filter(p => p.kind !== "video").length;
    const note = b.state.bs.planNote ? String(b.state.bs.planNote).replace(/\s*[\u2014\u2013]\s*/g, ", ") : "";
    return /*#__PURE__*/React.createElement("div", {
      className: "bm-list"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bm-list-head"
    }, /*#__PURE__*/React.createElement("b", null, "Pieces"), /*#__PURE__*/React.createElement("span", {
      className: "bm-addwrap"
    }, /*#__PURE__*/React.createElement("button", {
      className: "bm-link",
      "aria-haspopup": "menu",
      "aria-expanded": !!this.state.addOpen,
      title: "Add a piece Plunk left out: another place to post, or a video",
      onClick: () => this.setState({
        addOpen: !this.state.addOpen,
        rowMenu: null
      })
    }, "+ Add"), this.state.addOpen && this.renderAddMenu())), /*#__PURE__*/React.createElement(OwnerPicker, {
      b: b
    }), /*#__PURE__*/React.createElement("div", {
      className: "bm-rows"
    }, pieces.map(p => {
      const st = this.rowStatus(p);
      const [lbl, tone] = STATUS_CHIP[st] || ["", "idle"];
      const isSel = sel && sel.key === p.key;
      return /*#__PURE__*/React.createElement("div", {
        key: p.key,
        role: "button",
        tabIndex: 0,
        "aria-current": isSel ? "true" : undefined,
        className: "bm-row" + (isSel ? " on" : "") + (p.on ? "" : " off"),
        onClick: () => this.select(p),
        onKeyDown: e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.select(p);
          }
        }
      }, /*#__PURE__*/React.createElement("span", {
        className: "bm-row-text"
      }, /*#__PURE__*/React.createElement("b", null, this.pieceTitle(p)), /*#__PURE__*/React.createElement("span", null, kindOf(p)[0])), (() => {
        const v = this.variantFor(p);
        const os = v ? b.othersOn(v.id) : [];
        return os.length ? /*#__PURE__*/React.createElement("span", {
          className: "bm-here",
          title: os.map(o => `${nameOf(o.user)}${o.editing ? " is editing" : " is looking"}`).join(", ")
        }, os.slice(0, 2).map(o => /*#__PURE__*/React.createElement("span", {
          key: o.user,
          className: "bm-av" + (o.editing ? " editing" : "")
        }, initialsOf(o.user)))) : null;
      })(), /*#__PURE__*/React.createElement(AssigneeDots, {
        b: b,
        v: this.variantFor(p)
      }), /*#__PURE__*/React.createElement("span", {
        className: "bm-chip " + tone
      }, lbl), /*#__PURE__*/React.createElement("button", {
        className: "bm-row-more",
        title: "More for this piece",
        "aria-label": "More for this piece",
        onClick: e => {
          e.stopPropagation();
          this.setState({
            rowMenu: this.state.rowMenu === p.key ? null : p.key,
            addOpen: false
          });
        }
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "more_horiz")), this.state.rowMenu === p.key && /*#__PURE__*/React.createElement("div", {
        className: "bm-menu bm-rowmenu",
        role: "menu",
        onClick: e => e.stopPropagation()
      }, /*#__PURE__*/React.createElement("button", {
        role: "menuitem",
        onClick: () => {
          this.togglePiece(p);
          this.closeMenus();
        }
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, p.on ? "do_not_disturb_on" : "add_circle_outline"), p.on ? "Skip this piece" : "Include this piece")));
    }), !pieces.length && /*#__PURE__*/React.createElement("p", {
      className: "bm-quiet"
    }, "Nothing planned yet. Add a piece above, or go back to the Brief and pick where this story goes.")), note && /*#__PURE__*/React.createElement("p", {
      className: "bm-plan"
    }, /*#__PURE__*/React.createElement("b", null, "Plunk's plan:"), " ", note), /*#__PURE__*/React.createElement("div", {
      className: "bm-list-acts"
    }, /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm",
      disabled: !!this.state.allBusy || !on.length,
      title: "Writes every piece that is not written yet. Skipped pieces and videos are left as they are.",
      onClick: this.writeAll
    }, this.state.allBusy ? "Writing…" : "Write all"), b.reviewsOn() && /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm",
      disabled: !this.reviewable().length,
      title: "Ask teammates to sign off pieces",
      onClick: () => this.setState({
        rrOpen: {}
      })
    }, "Request review"), /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm quiet",
      disabled: !!b.state.busy.plan,
      title: "Ask Plunk for a fresh plan: new angles and pieces for anything not written yet. Written pieces keep their text.",
      onClick: () => b.planIt({
        stay: true
      })
    }, b.state.busy.plan ? "Planning…" : "Re-plan")), /*#__PURE__*/React.createElement("div", {
      className: "bm-progress"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bm-progress-row"
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, approved, " of ", total), " approved"), approved > 0 && /*#__PURE__*/React.createElement("button", {
      className: "bm-go sm",
      onClick: () => b.go("ship")
    }, "Go to Publish")), /*#__PURE__*/React.createElement("div", {
      className: "bm-meter",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("i", {
      style: {
        width: `${total ? Math.round(approved / total * 100) : 0}%`
      }
    }))));
  }
  renderExtras() {
    return /*#__PURE__*/React.createElement(React.Fragment, null, this.renderTemplatePreview(), this.renderMediaEditor(), this.state.rrOpen && /*#__PURE__*/React.createElement(RequestReviewSheet, {
      b: this.props.b,
      pieces: this.reviewable(),
      preselect: this.state.rrOpen.preselect,
      onClose: () => this.setState({
        rrOpen: null
      })
    }));
  }
  /** Written pieces of this story that can go to review (phase 4). */
  reviewable() {
    return this.pieces().filter(p => p.on && p.kind !== "video").map(p => ({
      p,
      v: this.variantFor(p)
    })).filter(x => x.v && !["published", "live"].includes(String(x.v.status || "").toLowerCase())).map(x => ({
      v: x.v,
      title: this.pieceTitle(x.p),
      kind: kindOf(x.p)[0]
    }));
  }
  renderMediaEditor() {
    const b = this.props.b;
    const m = this.state.mediaEdit;
    if (!m) return null;
    const close = () => this.setState({
      mediaEdit: null
    });
    const done = res => {
      const list = Array.isArray(res) ? res : [res];
      const v = list.filter(x => x && typeof x === "object" && x.url).pop();
      if (v && m.onDone) m.onDone(v.url);
      b.reloadDetails();
    };
    if (m.kind === "edit") return /*#__PURE__*/React.createElement(EditModeModal, {
      asset: m.url,
      runAgent: b.runAgent,
      waitAgent: b.waitAgent,
      creditsToast: (e, f) => b.agentError(e, f),
      onSaved: done,
      onClose: close
    });
    if (m.kind === "resize") return /*#__PURE__*/React.createElement(SmartResizeModal, {
      asset: m.url,
      runAgent: b.runAgent,
      waitAgent: b.waitAgent,
      creditsToast: (e, f) => b.agentError(e, f),
      onSaved: done,
      onClose: close
    });
    if (m.kind === "vedit") return /*#__PURE__*/React.createElement(VideoEditModal, {
      asset: m.asset,
      onSaved: done,
      onClose: close
    });
    if (m.kind === "trim") return /*#__PURE__*/React.createElement(TrimModal, {
      asset: m.asset,
      onSaved: done,
      onClose: close
    });
    if (m.kind === "vresize") return /*#__PURE__*/React.createElement(VideoResizeModal, {
      asset: m.asset,
      onSaved: done,
      onClose: close
    });
    return null;
  }
  // ---- Center: the piece as it will look, on a grey stage. Layout, export and the website frame stay as quiet controls above it.
  // ---- Notes on words (phase 3): selecting words in the preview offers a note anchored to them.
  pickQuote(p, sel, within) {
    if (!sel || sel.isCollapsed) return false;
    if (within && !(within.contains(sel.anchorNode) && within.contains(sel.focusNode))) return false;
    const text = String(sel.toString() || "").replace(/\s+/g, " ").trim();
    if (text.length < 2) return false;
    this.setState({
      quote: {
        key: p.key,
        text: text.slice(0, 500)
      }
    });
    return true;
  }
  /** Find a note's words in the preview and select them. */
  showInPiece(p, quote) {
    const ifr = this._iframes[p.key];
    const w = ifr && ifr.contentWindow ? ifr.contentWindow : window;
    try {
      const sel = w.getSelection();
      if (sel) sel.removeAllRanges();
      if (ifr && ifr.contentDocument && ifr.contentDocument.body) ifr.contentDocument.body.focus && ifr.contentDocument.body.focus();
      const found = typeof w.find === "function" && w.find(quote, false, false, true);
      if (!found) toast("Couldn't find those words in the preview.");else {
        const s2 = w.getSelection();
        const el = s2 && s2.anchorNode && s2.anchorNode.parentElement;
        if (el && el.scrollIntoView) el.scrollIntoView({
          block: "center"
        });
      }
    } catch (e) {
      toast("Couldn't find those words in the preview.");
    }
  }
  renderStage(p) {
    const b = this.props.b;
    const v = this.variantFor(p);
    const t = this.templateFor(p);
    const d = this.designFor(p, v);
    const owned = OWNED.has(p.channel);
    const canvas = this.state.canvas[p.key];
    const kind = kindOf(p);
    const angle = this.state.angleDraft != null ? this.state.angleDraft : p.row.angle || "";
    const caption = p.kind === "video" ? "Video · how it will play" : p.channel === "email" ? "Email · how it will look in the inbox" : `${kind[0]} · how it will look`;
    const isPage = p.kind !== "video" && owned && PAGE_CHANNELS.has(p.channel);
    const fr = isPage ? this.siteFrame() : null;
    const layouts = b.state.templates.filter(x => {
      const c = String(x.channel || "").toLowerCase();
      return c === (p.kind === "video" ? "video" : p.channel) || c === "any";
    });
    const editors = v ? b.othersOn(v.id).filter(o => o.editing) : [];
    return /*#__PURE__*/React.createElement("div", {
      className: "bm-stage-in"
    }, editors.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "bm-lockbar",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bm-av editing"
    }, initialsOf(editors[0].user)), /*#__PURE__*/React.createElement("span", null, nameOf(editors[0].user), editors.length > 1 ? ` and ${editors.length - 1} more` : "", " ", editors.length > 1 ? "are" : "is", " editing this piece. Leave a note, or wait: if you both save, you will be asked which version to keep.")), v && this.state.quote && this.state.quote.key === p.key && /*#__PURE__*/React.createElement("div", {
      className: "bm-quotebar",
      role: "status"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "format_quote"), /*#__PURE__*/React.createElement("q", null, this.state.quote.text.length > 120 ? this.state.quote.text.slice(0, 119) + "…" : this.state.quote.text), /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm",
      onClick: () => this.commentOnQuote(p, v)
    }, "Comment on these words"), /*#__PURE__*/React.createElement("button", {
      className: "bm-link mute",
      "aria-label": "Dismiss",
      onClick: () => this.setState({
        quote: null
      })
    }, "Dismiss")), /*#__PURE__*/React.createElement("div", {
      className: "bm-stage-top"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bm-caption"
    }, caption), /*#__PURE__*/React.createElement("span", {
      className: "bm-tools"
    }, /*#__PURE__*/React.createElement("label", {
      className: "bm-field",
      title: "The layout this piece is made on"
    }, /*#__PURE__*/React.createElement("span", null, "Layout"), /*#__PURE__*/React.createElement("select", {
      value: t ? String(t.id) : "",
      onChange: e => this.setTemplate(p, e.target.value)
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "No layout"), layouts.map(x => /*#__PURE__*/React.createElement("option", {
      key: String(x.id),
      value: String(x.id)
    }, humanName(x.name), x.scope === "plunk" ? " · starter" : x.scope === "story" ? " · this story" : "")), /*#__PURE__*/React.createElement("option", {
      value: "__ref"
    }, "Make one from an example\u2026"))), isPage && v && /*#__PURE__*/React.createElement("label", {
      className: "bm-field",
      title: "Preview and export this page for your website"
    }, /*#__PURE__*/React.createElement("span", null, "Export"), /*#__PURE__*/React.createElement("select", {
      value: "",
      onChange: e => {
        const k = e.target.value;
        if (k) this.exportPage(p, v, k);
      }
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "Choose\u2026"), /*#__PURE__*/React.createElement("option", {
      value: "open"
    }, "Open as a page"), /*#__PURE__*/React.createElement("option", {
      value: "html"
    }, "Download HTML"), /*#__PURE__*/React.createElement("option", {
      value: "pdf"
    }, "Print or save as PDF"), /*#__PURE__*/React.createElement("option", {
      value: "png"
    }, "Save as a picture"))), isPage && /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm quiet" + (fr && this.state.frameOn !== false ? " active" : ""),
      title: "Show this page inside your website's own header, menu and footer",
      onClick: () => this.setState({
        frameEdit: this.state.frameEdit ? null : {
          url: fr && fr.url || this.firstSourceUrl()
        }
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "web"), fr ? fr.host : "Website frame"), p.kind !== "video" && v && p.channel === "ad" && /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm quiet",
      title: "Open the flight builder with this headline, description and picture filled in",
      onClick: () => this.sendToFlight(p, v)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "flight_takeoff"), "Send to a flight"))), /*#__PURE__*/React.createElement("div", {
      className: "bm-lead"
    }, /*#__PURE__*/React.createElement("label", {
      htmlFor: `bm-lead-${p.key}`
    }, "Lead with"), /*#__PURE__*/React.createElement("input", {
      id: `bm-lead-${p.key}`,
      value: angle,
      placeholder: "What this piece should open with. Plunk suggested this; change it if you like.",
      onChange: e => this.setState({
        angleDraft: e.target.value
      }),
      onBlur: () => {
        if (this.state.angleDraft != null && this.state.angleDraft !== (p.row.angle || "")) this.setAngle(p, this.state.angleDraft);
        this.setState({
          angleDraft: null
        });
      }
    })), this.renderFrameRow(p), p.kind === "video" ? this.renderVideoStage(p) : owned && d ? /*#__PURE__*/React.createElement(React.Fragment, null, this.renderEmptyStrip(p, d), /*#__PURE__*/React.createElement("div", {
      className: "bm-card hub-make-stage " + (p.channel === "email" ? "email" : "page")
    }, canvas && canvas.html ? /*#__PURE__*/React.createElement("iframe", {
      title: `${kind[0]} preview`,
      sandbox: "allow-same-origin",
      srcDoc: isPage && this.state.frameOn !== false ? wrapFrame(canvas.html, this.frameData(), true) : canvas.html,
      ref: el => {
        this._iframes[p.key] = el;
      },
      onLoad: e => this.wireCanvas(p, e.target)
    }) : /*#__PURE__*/React.createElement("div", {
      className: "bm-card-wait"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons bm-spin"
    }, "autorenew"), "Putting the preview together\u2026"), this.renderHoverBar(p, d), this.renderPop(p, d)), /*#__PURE__*/React.createElement("p", {
      className: "bm-stage-hint"
    }, "Click any words in the preview to change them; click a picture to swap it.")) : this.renderTextPiece(p, v));
  }
  // ---- Right: what to do with this piece.
  sectionOpen(k, dflt) {
    const o = this.state.open || {};
    return k in o ? !!o[k] : !!dflt;
  }
  fold(k, title, meta, body, dflt) {
    const on = this.sectionOpen(k, dflt);
    return /*#__PURE__*/React.createElement("div", {
      className: "bm-fold" + (on ? " on" : ""),
      key: k
    }, /*#__PURE__*/React.createElement("button", {
      className: "bm-fold-head",
      "aria-expanded": on,
      onClick: () => this.toggleSection(k, dflt)
    }, /*#__PURE__*/React.createElement("b", null, title), meta ? /*#__PURE__*/React.createElement("i", null, meta) : null, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, on ? "expand_less" : "expand_more")), on && /*#__PURE__*/React.createElement("div", {
      className: "bm-fold-body"
    }, body));
  }
  /** The image's asset id when the story's assets know it; looked up once per new picture. */
  assetIdFor(url) {
    if (!url) return null;
    const map = this.state.assetIds || {};
    if (map[url]) return map[url];
    this._assetTried = this._assetTried || new Set();
    if (!this._assetTried.has(url)) {
      this._assetTried.add(url);
      setTimeout(() => this._alive && this.loadAssetIds(), 0);
    }
    return null;
  }
  renderPanel(p) {
    if (p.kind === "video") return this.renderVideoControls(p);
    const b = this.props.b;
    const v = this.variantFor(p);
    const d = this.designFor(p, v);
    const owned = OWNED.has(p.channel);
    const kind = kindOf(p);
    const noun = kind[1];
    const st = v ? String(v.status || "").toLowerCase() : "";
    const designed = owned && !!d;
    const empties = designed ? this.emptySlots(d) : [];
    const busy = !!this.state.busy[`fill:${p.key}`] || !!this.state.busy[`slot:${p.key}`] || !!this.state.busy[`text:${p.key}`];
    const note = String((this.state.ask || {})[p.key] || "");
    const editText = () => {
      if (designed) {
        this.setState({
          partsOpen: this.state.partsOpen === p.key ? null : p.key
        });
        return;
      }
      if (!v) return;
      this.setState({
        edit: {
          key: p.key,
          i: -1,
          form: {
            title: v.title || "",
            body: v.body || ""
          }
        },
        pick: null,
        gen: null,
        popAt: {
          top: 8,
          left: 14
        }
      });
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "bm-panel-in"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bm-sec"
    }, !v ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
      className: "bm-approve",
      disabled: busy,
      onClick: () => designed ? this.fill(p, empties.length ? empties : null) : this.writeText(p, false)
    }, busy ? "Writing…" : `Write this ${noun}`), /*#__PURE__*/React.createElement("span", {
      className: "bm-sub"
    }, busy ? "It lands here within a minute." : "Plunk writes it from the story. You approve it before anything goes out.")) : st === "approved" ? /*#__PURE__*/React.createElement("div", {
      className: "bm-done"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "check_circle"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "Approved"), /*#__PURE__*/React.createElement("i", null, "Ready to publish.")), /*#__PURE__*/React.createElement("button", {
      className: "bm-link",
      onClick: () => b.setVariantStatus(v, "draft")
    }, "Back to review")) : st === "published" || st === "live" ? /*#__PURE__*/React.createElement("div", {
      className: "bm-done"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "check_circle"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "Published"), /*#__PURE__*/React.createElement("i", null, "Changes here can go out again from Publish."))) : b.approvalLocked() ?
    // team workspace (phase 4): a moderator approves; everyone else asks for a review
    b.reviewState(v) ? /*#__PURE__*/React.createElement("span", {
      className: "bm-sub left"
    }, b.reviewState(v) === "signed" ? "Everyone signed off. A moderator approves it next." : b.reviewState(v) === "changes" ? "A reviewer asked for changes. See Review below." : "Waiting on the reviewers. See Review below.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
      className: "bm-approve",
      onClick: () => this.setState({
        rrOpen: {
          preselect: String(v.id)
        }
      })
    }, "Ask for a review"), /*#__PURE__*/React.createElement("span", {
      className: "bm-sub"
    }, "In this workspace a moderator approves pieces.")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
      className: "bm-approve",
      disabled: busy,
      onClick: () => this.approve(p)
    }, `Approve this ${noun}`), /*#__PURE__*/React.createElement("span", {
      className: "bm-sub"
    }, b.reviewsOn() && b.reviewState(v) !== "signed" ? "Approving before everyone signs off is noted in Activity." : "Approved pieces are ready to publish."), b.reviewsOn() && !b.reviewState(v) && /*#__PURE__*/React.createElement("button", {
      className: "bm-link",
      onClick: () => this.setState({
        rrOpen: {
          preselect: String(v.id)
        }
      })
    }, "Ask for a review first"))), v && b.teamOn() && /*#__PURE__*/React.createElement(PeopleRow, {
      b: b,
      v: v
    }), v && b.reviewsOn() && /*#__PURE__*/React.createElement(ReviewBox, {
      b: b,
      v: v
    }), v && /*#__PURE__*/React.createElement("div", {
      className: "bm-sec"
    }, /*#__PURE__*/React.createElement("label", {
      className: "bm-h",
      htmlFor: `bm-ask-${p.key}`
    }, "Ask for changes"), /*#__PURE__*/React.createElement("textarea", {
      id: `bm-ask-${p.key}`,
      rows: 3,
      value: note,
      placeholder: "e.g. Shorter, and mention free returns",
      onChange: e => this.setState({
        ask: {
          ...(this.state.ask || {}),
          [p.key]: e.target.value
        }
      })
    }), /*#__PURE__*/React.createElement("div", {
      className: "bm-row-acts"
    }, /*#__PURE__*/React.createElement("button", {
      className: "bm-btn",
      disabled: busy,
      onClick: () => this.rewrite(p)
    }, busy ? "Rewriting…" : "Rewrite"), /*#__PURE__*/React.createElement("button", {
      className: "bm-link mute",
      onClick: editText
    }, "Edit the text myself")), /*#__PURE__*/React.createElement("span", {
      className: "bm-sub left"
    }, designed ? `Plunk rewrites every part from the story${note.trim() ? ", following your note" : ""}.` : `Plunk writes a fresh version from the story${note.trim() ? ", following your note" : ""}. The current one is kept in Earlier versions.`), designed && empties.length > 0 && /*#__PURE__*/React.createElement("button", {
      className: "bm-link",
      disabled: busy,
      onClick: () => this.fill(p, empties)
    }, "Write the ", empties.length, " empty part", empties.length === 1 ? "" : "s", " only"), designed && this.state.partsOpen === p.key && /*#__PURE__*/React.createElement("div", {
      className: "bm-parts"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bm-sub left"
    }, "Pick a part to edit, or click the words in the preview."), d.sections.map((s, i) => [s, i]).filter(([s]) => TEXT_TYPES.has(String(s.type || "").toLowerCase())).map(([s, i]) => {
      const ty = String(s.type || "").toLowerCase();
      const txt = strip(s.headline || s.html || (s.items || []).join(", ") || s.label || "");
      return /*#__PURE__*/React.createElement("button", {
        key: i,
        className: "bm-part",
        onClick: () => {
          this.scrollCanvasTo(p, i);
          this.openPop(p, i, "edit", null);
        }
      }, /*#__PURE__*/React.createElement("b", null, SLOT_LABEL[ty] || ty), /*#__PURE__*/React.createElement("span", null, txt ? txt.slice(0, 60) : "Empty"));
    }))), this.renderPicture(p, v, d), v && this.renderMore(p, v), v && this.renderChecks(p, v, d));
  }
  renderPicture(p, v, d) {
    const b = this.props.b;
    const owned = OWNED.has(p.channel);
    let url = null;
    let isVideo = false;
    let change = null;
    let gen = null;
    let busy = false;
    if (owned && d) {
      const i = d.sections.findIndex(s => IMAGE_TYPES.has(String(s.type || "").toLowerCase()));
      if (i < 0) return null;
      const s = d.sections[i];
      url = s.imageUrl || s.images && s.images[0] || null;
      busy = this.genBusy(p, i);
      change = () => {
        this.scrollCanvasTo(p, i);
        this.openPop(p, i, "pick", null);
      };
      gen = () => {
        this.scrollCanvasTo(p, i);
        this.openPop(p, i, "gen", null);
      };
    } else if (!owned) {
      const pick = v ? b.socialMediaFor(v, this.detailFor(v)) : {
        imageUrl: b.storyImages()[0] || null
      };
      url = pick.videoUrl || pick.imageUrl || null;
      isVideo = !!pick.videoUrl;
      busy = this.genBusy(p, -1);
      change = v ? () => this.setState({
        pick: {
          key: p.key,
          i: -1
        },
        edit: null,
        gen: null,
        popAt: {
          top: 8,
          left: 14
        }
      }) : null;
      gen = v ? () => this.setState({
        gen: {
          key: p.key,
          i: -1,
          kind: "hero",
          direction: ""
        },
        edit: null,
        pick: null,
        popAt: {
          top: 8,
          left: 14
        }
      }) : null;
    } else return null;
    const aid = !isVideo ? this.assetIdFor(url) : null;
    return /*#__PURE__*/React.createElement("div", {
      className: "bm-sec"
    }, /*#__PURE__*/React.createElement("b", {
      className: "bm-h"
    }, isVideo ? "Video" : "Picture"), /*#__PURE__*/React.createElement("div", {
      className: "bm-pic"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bm-pic-thumb" + (url ? "" : " none")
    }, url ? isVideo ? /*#__PURE__*/React.createElement("video", {
      src: url,
      poster: this.props.b.posterFor ? this.props.b.posterFor(url) : undefined,
      muted: true,
      playsInline: true,
      preload: "metadata"
    }) : /*#__PURE__*/React.createElement("img", {
      src: url,
      alt: ""
    }) : /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "image")), /*#__PURE__*/React.createElement("span", {
      className: "bm-pic-acts"
    }, change && /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm",
      disabled: busy,
      title: url ? "Pick another picture from this story or Assets" : "Pick a picture from this story or Assets",
      onClick: change
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "swap_horiz"), url ? "Change" : "Add"), gen && /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm",
      disabled: busy,
      title: "Plunk makes a new picture for this piece",
      onClick: gen
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "auto_awesome"), busy ? "Making…" : "New picture"), aid && /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm",
      title: "Open it in the image editor: retouch, background, crop, text",
      onClick: () => this.openImageEditor(aid)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "tune"), "Edit"))), !v && !owned && /*#__PURE__*/React.createElement("span", {
      className: "bm-sub left"
    }, "Write the piece first, then pick its picture."));
  }
  renderMore(p, v) {
    const b = this.props.b;
    const ch = p.channel;
    const id = String(v.id);
    const lm = b.localeFor(v);
    const opts = lm ? [] : b.localeOptions();
    const busyTr = !!b.state.busy[`tr:${v.id}`];
    const hs = b.historyFor(v);
    const threads = b.notesFor(v);
    const openNotes = threads.filter(t => !t.resolvedAt).length;
    const notes = threads.reduce((a, t) => a.concat([t], t.replies), []);
    const parts = [];
    if (ch === "email") parts.push(this.fold(`em:${id}`, "Subject and preview text", null, b.renderEmailExtras(v)));
    if (SEO_CHANNELS.has(ch)) parts.push(this.fold(`seo:${id}`, "Search listing", b.seoFor(v) ? "written" : null, b.renderSeo(v)));
    if (opts.length > 0) parts.push(this.fold(`tr:${id}`, "Translate", null, /*#__PURE__*/React.createElement("label", {
      className: "bm-field block"
    }, /*#__PURE__*/React.createElement("span", null, "Write a version in another language"), /*#__PURE__*/React.createElement("select", {
      value: "",
      disabled: busyTr,
      onChange: e => b.translateFor(v, opts.find(o => o.locale === e.target.value))
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, busyTr ? "Translating…" : "Choose a language…"), opts.map(o => /*#__PURE__*/React.createElement("option", {
      key: o.locale,
      value: o.locale
    }, o.label))))));
    if (hs.length) parts.push(this.fold(`hist:${id}`, "Earlier versions", String(hs.length), /*#__PURE__*/React.createElement("div", {
      className: "bm-list-plain"
    }, hs.map((h, i) => {
      // each history entry is the text BEFORE a save; h.by saved over it. Compare shows what changed since then.
      const cmpKey = `${id}:${i}`;
      const open = this.state.cmp === cmpKey;
      return /*#__PURE__*/React.createElement("div", {
        key: i,
        className: "bm-hist"
      }, /*#__PURE__*/React.createElement("span", {
        className: "bm-sub left"
      }, h.at ? new Date(h.at).toLocaleString() : "", h.by ? ` · replaced by ${nameOf(h.by)}` : ""), /*#__PURE__*/React.createElement("div", {
        className: "bm-hist-text"
      }, h.title ? /*#__PURE__*/React.createElement("b", null, strip(h.title), ": ") : null, strip(h.body).slice(0, 240)), /*#__PURE__*/React.createElement("span", {
        className: "bm-row-acts"
      }, /*#__PURE__*/React.createElement("button", {
        className: "bm-link",
        "aria-expanded": open,
        onClick: () => this.setState({
          cmp: open ? null : cmpKey
        })
      }, open ? "Hide changes" : "Compare with now"), /*#__PURE__*/React.createElement("button", {
        className: "bm-link",
        onClick: () => b.restoreVersion(v, h)
      }, "Bring this version back")), open && /*#__PURE__*/React.createElement("div", {
        className: "bm-diff",
        "aria-label": "Changes from this version to now"
      }, h.title !== v.title && /*#__PURE__*/React.createElement("div", {
        className: "bm-diff-title"
      }, wordDiff(strip(h.title), strip(v.title)).map((d, k) => d.op === "same" ? /*#__PURE__*/React.createElement("span", {
        key: k
      }, d.t) : d.op === "add" ? /*#__PURE__*/React.createElement("ins", {
        key: k
      }, d.t) : /*#__PURE__*/React.createElement("del", {
        key: k
      }, d.t))), wordDiff(plainBody(h.body), plainBody(v.body)).map((d, k) => d.op === "same" ? /*#__PURE__*/React.createElement("span", {
        key: k
      }, d.t) : d.op === "add" ? /*#__PURE__*/React.createElement("ins", {
        key: k
      }, d.t) : /*#__PURE__*/React.createElement("del", {
        key: k
      }, d.t))));
    }))));
    // Activity on this piece: saves, notes and publishes, newest first (phase 1; assignments and reviews join later).
    const pubs = ((this.detailFor(v) || {}).publications || []).filter(x => String(x.variantId) === id);
    const acts = [...hs.map(h => ({
      at: h.at,
      by: h.by,
      what: "saved a new version"
    })), ...notes.map(c => ({
      at: c.at,
      by: c.by,
      what: `${c.parentId ? "replied" : "left a note"}: "${String(c.text || "").slice(0, 80)}${String(c.text || "").length > 80 ? "…" : ""}"`
    })), ...threads.filter(t => t.resolvedAt).map(t => ({
      at: t.resolvedAt,
      by: t.resolvedBy,
      what: "resolved a thread"
    })), ...(b.state.reviews && b.state.reviews.reviews || []).flatMap(r => (r.votes || []).filter(x => String(x.variantId) === id).map(x => ({
      r,
      x
    }))).flatMap(({
      r,
      x
    }) => [x.votedDate && x.vote !== "pending" ? {
      at: x.votedDate,
      by: x.reviewer,
      what: x.vote === "signed_off" ? "signed off" : `asked for changes: "${String(x.note || "").slice(0, 80)}"`
    } : null]).concat((b.state.reviews && b.state.reviews.reviews || []).filter(r => (r.votes || []).some(x => String(x.variantId) === id)).map(r => ({
      at: r.createdDate,
      by: r.createdBy,
      what: "asked for a review"
    }))).filter(Boolean), ...b.blocksOf(v.piece ? v.piece.id : v.containerId).filter(x => x.kind === "approval").map(x => parseJson(x.body)).filter(a => a && String(a.variantId || "") === id).map(a => ({
      at: a.at,
      by: a.by,
      what: a.auto ? "approved (everyone signed off)" : a.skippedReview && b.teamOn() ? "approved without a finished review" : "approved"
    })), ...b.blocksOf(v.piece ? v.piece.id : v.containerId).filter(x => x.kind === "assignment").map(x => parseJson(x.body)).filter(a => a && String(a.variantId || "") === id).map(a => ({
      at: a.at,
      by: a.by,
      what: a.assignee ? `assigned the ${a.role === "media" ? "pictures" : "words"} to ${nameOf(a.assignee)}${a.due ? `, due ${a.due}` : ""}` : `cleared who's on the ${a.role === "media" ? "pictures" : "words"}`
    })), ...pubs.map(x => ({
      at: x.publishedDate || x.createdDate,
      by: null,
      what: `published to ${String(x.target || "").replace(/_/g, " ")}`
    }))].filter(x => x.at).sort((a, c) => new Date(c.at) - new Date(a.at));
    if (acts.length) parts.push(this.fold(`act:${id}`, "Activity", String(acts.length), /*#__PURE__*/React.createElement("div", {
      className: "bm-list-plain"
    }, acts.slice(0, 30).map((a, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "bm-act"
    }, /*#__PURE__*/React.createElement("span", {
      className: "bm-av"
    }, a.by ? initialsOf(a.by) : "P"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, a.by ? nameOf(a.by) : "Plunk"), " ", a.what), /*#__PURE__*/React.createElement("span", {
      className: "bm-sub"
    }, new Date(a.at).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit"
    })))))));
    const anchor = (this.state.noteAnchor || {})[id] || null;
    parts.push(this.fold(`cmt:${id}`, "Notes for the team", openNotes ? String(openNotes) : null, /*#__PURE__*/React.createElement(NotesPanel, {
      ref: el => {
        this._notes = el;
      },
      b: b,
      v: v,
      plain: plainBody(v.body) + " " + strip(v.title),
      anchor: anchor,
      onClearAnchor: () => this.setState({
        noteAnchor: {
          ...(this.state.noteAnchor || {}),
          [id]: null
        }
      }),
      onShow: q => this.showInPiece(p, q)
    }), !!anchor));
    return /*#__PURE__*/React.createElement("div", {
      className: "bm-sec bm-more"
    }, parts, /*#__PURE__*/React.createElement("button", {
      className: "bm-link mute",
      title: "Keep the shape of this piece as a layout for the next story",
      onClick: () => b.setState({
        tplSheet: {
          kind: "variant",
          variantId: id,
          channel: ch
        }
      })
    }, "Save as a layout for next time"));
  }
  renderChecks(p, v, d) {
    const b = this.props.b;
    const id = String(v.id);
    const rows = [];
    if (OWNED.has(p.channel) && d) {
      const n = this.emptySlots(d).length;
      rows.push(n ? ["warn", `${n} part${n === 1 ? " is" : "s are"} still empty`] : ["ok", "Every part is written"]);
    }
    if (MEDIA_REQUIRED.has(p.channel)) {
      const pick = b.socialMediaFor(v, this.detailFor(v));
      rows.push(pick.imageUrl || pick.videoUrl ? ["ok", "Has a picture or video"] : ["warn", `${kindOf(p)[0].replace(/ (post|pin)$/, "")} needs a picture or video`]);
    }
    const c = (this.state.checks || {})[id];
    const fresh = c && c.body === v.body && c.title === v.title ? c.s : null;
    const checking = this.state.checking === id;
    return /*#__PURE__*/React.createElement("div", {
      className: "bm-sec bm-checks"
    }, /*#__PURE__*/React.createElement("b", {
      className: "bm-h"
    }, "Checks"), rows.map(([tone, text], i) => /*#__PURE__*/React.createElement("span", {
      key: i,
      className: "bm-check " + tone
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, tone === "ok" ? "check" : "error_outline"), text)), fresh && fresh.error && /*#__PURE__*/React.createElement("span", {
      className: "bm-check idle"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "help_outline"), "The brand check did not finish. Try again."), fresh && !fresh.error && !(fresh.issues || []).length && /*#__PURE__*/React.createElement("span", {
      className: "bm-check ok"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "check"), "Fits your brand rules"), fresh && !fresh.error && (fresh.issues || []).map((i, k) => {
      const crit = String(i.severity || "").toLowerCase() === "critical";
      return /*#__PURE__*/React.createElement("span", {
        key: k,
        className: "bm-check " + (crit ? "bad" : "warn")
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, crit ? "block" : "error_outline"), /*#__PURE__*/React.createElement("span", null, i.text, i.fix ? /*#__PURE__*/React.createElement("i", null, " ", i.fix) : null));
    }), /*#__PURE__*/React.createElement("button", {
      className: "bm-link",
      disabled: checking,
      onClick: () => this.runBrandCheck(v)
    }, checking ? "Checking…" : fresh ? "Check against your brand rules again" : "Check against your brand rules"), !fresh && !checking && /*#__PURE__*/React.createElement("span", {
      className: "bm-sub left"
    }, "Looks for words and claims your brand settings rule out."));
  }
  /** Pour the current copy into another template's layout: each new section takes the first unused old section of the same type. */
  mapDesign(oldD, newD) {
    const used = new Set();
    const take = ty => {
      const i = oldD.sections.findIndex((x, k) => !used.has(k) && String(x.type || "").toLowerCase() === ty);
      if (i < 0) return null;
      used.add(i);
      return oldD.sections[i];
    };
    const FIELDS = ["headline", "subhead", "html", "label", "url", "imageUrl", "imageFrom", "images", "items", "code"];
    const sections = newD.sections.map(n => {
      const o = take(String(n.type || "").toLowerCase());
      if (!o) return {
        ...n
      };
      const out = {
        ...n
      };
      FIELDS.forEach(f => {
        if (o[f] != null && o[f] !== "" && !(Array.isArray(o[f]) && !o[f].length)) out[f] = o[f];
      });
      return out;
    });
    const spare = oldD.sections.filter((x, k) => !used.has(k) && x.imageUrl).map(x => x.imageUrl);
    sections.forEach(n => {
      if (spare.length && IMAGE_TYPES.has(String(n.type || "").toLowerCase()) && !n.imageUrl && !(n.images && n.images.length)) {
        if (String(n.type).toLowerCase() === "gallery") n.images = spare.splice(0, 3);else n.imageUrl = spare.shift();
      }
    });
    return {
      ...newD,
      sections
    };
  }
  renderTemplatePreview() {
    const tp = this.state.tplPreview;
    if (!tp) return null;
    const close = () => this.setState({
      tplPreview: null
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-modal-backdrop hub-edit-backdrop",
      onClick: close
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-trash-sheet hub-tplswitch",
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-tplp-head"
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("h3", {
      className: "hub-h3",
      style: {
        margin: 0
      }
    }, "Switch to ", humanName(tp.t.name), "?"), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "Your copy and images poured into the new layout. Nothing changes until you choose.")), /*#__PURE__*/React.createElement("span", {
      className: "hub-tplp-acts"
    }, /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle sm",
      style: {
        margin: 0
      },
      onClick: close
    }, "Keep current"), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary",
      disabled: tp.html == null,
      onClick: this.applyTemplatePreview
    }, "Use this layout"))), /*#__PURE__*/React.createElement("div", {
      className: "hub-tplswitch-stage"
    }, tp.html == null ? /*#__PURE__*/React.createElement("div", {
      className: "hub-bld-empty"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "hourglass_top"), "Rendering the preview\u2026") : /*#__PURE__*/React.createElement("iframe", {
      title: "Preview",
      sandbox: "",
      srcDoc: tp.html
    }))));
  }
  renderEmptyStrip(p, d) {
    const empty = this.emptySlots(d);
    if (!empty.length) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-make-emptystrip"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "edit_note"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, empty.length, " part", empty.length === 1 ? "" : "s", " still empty:"), " ", empty.map(i => /*#__PURE__*/React.createElement("button", {
      key: i,
      className: "territory-toggle xs",
      onClick: () => this.openPop(p, i, "edit", null)
    }, SLOT_LABEL[String(d.sections[i].type || "").toLowerCase()] || d.sections[i].type))), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: "0 0 0 auto"
      }
    }, "or let Plunk write them: Write the empty parts, on the right"));
  }
  renderPop(p, d) {
    const e = this.state.edit;
    const pk = this.state.pick;
    const g = this.state.gen;
    const which = e && e.key === p.key ? "edit" : g && g.key === p.key ? "gen" : pk && pk.key === p.key ? "pick" : null;
    if (!which || !d) return null;
    const i = which === "edit" ? e.i : which === "gen" ? g.i : pk.i;
    const s = d.sections[i] || {};
    const ty = String(s.type || "").toLowerCase();
    const at = this.state.popAt || {
      top: 8,
      left: 14
    };
    const busy = !!this.state.busy[`fill:${p.key}`] || !!this.state.busy[`slot:${p.key}`] || this.genBusy(p, i);
    const inp = {
      ...INPUT,
      height: "32px",
      padding: "0 9px",
      marginTop: 4
    };
    let body = null;
    if (which === "edit") {
      const f = e.form;
      const setF = patch => this.setState({
        edit: {
          ...this.state.edit,
          form: {
            ...f,
            ...patch
          }
        }
      });
      body = /*#__PURE__*/React.createElement("div", {
        className: "hub-make-slot-edit",
        style: {
          margin: 0,
          padding: 0,
          border: 0
        }
      }, (ty === "hero" || ty === "heading" || ty === "imagetext" || ty === "offer" || ty === "features" || ty === "specs" || ty === "quote") && /*#__PURE__*/React.createElement("input", {
        value: f.headline,
        placeholder: "Headline",
        onChange: ev => setF({
          headline: ev.target.value
        }),
        style: inp
      }), (ty === "hero" || ty === "heading") && /*#__PURE__*/React.createElement("input", {
        value: f.subhead,
        placeholder: "Subhead",
        onChange: ev => setF({
          subhead: ev.target.value
        }),
        style: inp
      }), (ty === "text" || ty === "imagetext" || ty === "offer" || ty === "quote") && /*#__PURE__*/React.createElement("textarea", {
        rows: 5,
        value: f.html,
        placeholder: "Copy (simple HTML allowed)",
        onChange: ev => setF({
          html: ev.target.value
        }),
        style: {
          ...INPUT,
          marginTop: 4,
          resize: "vertical"
        }
      }), (ty === "features" || ty === "specs") && /*#__PURE__*/React.createElement("textarea", {
        rows: 5,
        value: f.items,
        placeholder: ty === "specs" ? "One per line, Label: value" : "One per line",
        onChange: ev => setF({
          items: ev.target.value
        }),
        style: {
          ...INPUT,
          marginTop: 4,
          resize: "vertical"
        }
      }), (ty === "hero" || ty === "button" || ty === "offer" || ty === "imagetext" || ty === "quote") && /*#__PURE__*/React.createElement("input", {
        value: f.label,
        placeholder: ty === "quote" ? "Attribution" : "Button label",
        onChange: ev => setF({
          label: ev.target.value
        }),
        style: inp
      }), (ty === "hero" || ty === "button" || ty === "offer" || ty === "imagetext") && /*#__PURE__*/React.createElement("input", {
        value: f.url,
        placeholder: "Button link (optional)",
        onChange: ev => setF({
          url: ev.target.value
        }),
        style: inp
      }), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 6,
          marginTop: 8
        }
      }, /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary",
        disabled: busy,
        onClick: () => this.saveSlot(p, i, {
          headline: f.headline,
          subhead: f.subhead,
          html: f.html,
          label: f.label,
          url: f.url,
          items: f.items ? f.items.split("\n").map(x => x.trim()).filter(Boolean) : undefined
        })
      }, busy ? "Saving…" : "Save"), TEXT_TYPES.has(ty) && /*#__PURE__*/React.createElement("button", {
        className: "territory-toggle sm",
        style: {
          margin: 0
        },
        disabled: busy,
        onClick: () => this.fill(p, [i])
      }, "Rewrite from the story"), /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-sm k-rounded-md k-button-flat",
        onClick: this.closePop
      }, "Cancel")));
    } else if (which === "gen") body = this.renderGenPopover(p, i);else body = /*#__PURE__*/React.createElement(MediaPicker, {
      b: this.props.b,
      current: s.imageUrl || s.images && s.images[0] || null,
      allowVideo: false,
      drafts: this.pieces().filter(x => x.kind === "draft" && x.key !== p.key),
      useOn: (u, k) => this.useImageOn(u, k),
      onPick: x => this.saveSlot(p, i, ty === "gallery" ? {
        images: [...(s.images || []), x.url].slice(-3)
      } : {
        imageUrl: x.url,
        imageFrom: "url"
      })
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pop" + (which === "pick" ? " wide" : ""),
      style: {
        top: at.top,
        left: at.left
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pop-head"
    }, /*#__PURE__*/React.createElement("b", null, SLOT_LABEL[ty] || ty), /*#__PURE__*/React.createElement("span", null, which === "edit" ? "Edit the fields" : which === "gen" ? "Generate an image" : "Pick an image"), /*#__PURE__*/React.createElement("button", {
      className: "material-icons",
      title: "Close",
      onClick: this.closePop
    }, "close")), body);
  }
  renderHoverBar(p, d) {
    const h = this.state.hover;
    if (!h || h.key !== p.key || !d || !d.sections[h.i]) return null;
    const i = h.i;
    const s = d.sections[i];
    const ty = String(s.type || "").toLowerCase();
    const hasImg = !!(s.imageUrl || s.images && s.images[0]);
    const busy = !!this.state.busy[`fill:${p.key}`] || !!this.state.busy[`slot:${p.key}`] || this.genBusy(p, i);
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-make-hover",
      style: {
        top: Math.max(2, Math.min(h.top - 30, (this._iframes[p.key] && this._iframes[p.key].parentElement ? this._iframes[p.key].parentElement.clientHeight : 900) - 36)),
        left: Math.max(14, Math.min(h.left, (this._iframes[p.key] && this._iframes[p.key].parentElement ? this._iframes[p.key].parentElement.clientWidth : 600) - 236))
      },
      onMouseEnter: () => {
        this._overBar = true;
        clearTimeout(this._hoverOff);
      },
      onMouseLeave: () => {
        this._overBar = false;
        if (!h.pin) this._hoverOff = setTimeout(() => this._alive && this.setState({
          hover: null
        }), 220);
      }
    }, /*#__PURE__*/React.createElement("b", null, SLOT_LABEL[ty] || ty), TEXT_TYPES.has(ty) && /*#__PURE__*/React.createElement("button", {
      title: "Rewrite the copy",
      disabled: busy,
      onClick: () => this.fill(p, [i])
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "auto_fix_high")), TEXT_TYPES.has(ty) && /*#__PURE__*/React.createElement("button", {
      title: "Edit the fields",
      onClick: () => this.openPop(p, i, "edit", h)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "edit_note")), IMAGE_TYPES.has(ty) && /*#__PURE__*/React.createElement("button", {
      title: "Pick an image",
      disabled: busy,
      onClick: () => this.openPop(p, i, "pick", h)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "photo_library")), IMAGE_TYPES.has(ty) && /*#__PURE__*/React.createElement("button", {
      title: "Generate an image",
      disabled: busy,
      onClick: () => this.openPop(p, i, "gen", h)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "auto_awesome")), IMAGE_TYPES.has(ty) && hasImg && /*#__PURE__*/React.createElement("button", {
      title: "Edit image (Edit Mode)",
      onClick: () => this.setState({
        hover: null,
        mediaEdit: {
          kind: "edit",
          url: s.imageUrl || s.images[0],
          onDone: url => this.saveSlot(p, i, ty === "gallery" ? {
            images: [url, ...(s.images || []).slice(1)]
          } : {
            imageUrl: url,
            imageFrom: "url"
          })
        }
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "tune")), IMAGE_TYPES.has(ty) && hasImg && /*#__PURE__*/React.createElement("button", {
      title: "Smart Resize",
      onClick: () => this.setState({
        hover: null,
        mediaEdit: {
          kind: "resize",
          url: s.imageUrl || s.images[0]
        }
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "aspect_ratio")));
  }
  renderTextPiece(p, v) {
    const b = this.props.b;
    const d = v ? this.detailFor(v) : {};
    const pick = v ? b.socialMediaFor(v, d) : {
      imageUrl: b.storyImages()[0] || null
    };
    const social = !OWNED.has(p.channel);
    const link = d.container && d.container.url || null;
    const saveBody = async text => {
      if (!v) return;
      if (!(await b.saveText(v, {
        title: v.title,
        body: text
      }))) return;
      await b.reloadDetails();
      toast("Saved.");
    };
    const tools = [];
    if (social && v && pick.imageUrl && !pick.videoUrl) {
      tools.push(/*#__PURE__*/React.createElement("button", {
        key: "e",
        className: "bm-btn sm quiet",
        title: "Touch up the picture",
        onClick: () => this.setState({
          mediaEdit: {
            kind: "edit",
            url: pick.imageUrl,
            onDone: async url => {
              await b.setSocialMedia(v, {
                imageUrl: url
              });
            }
          }
        })
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "tune"), "Touch up"));
      tools.push(/*#__PURE__*/React.createElement("button", {
        key: "r",
        className: "bm-btn sm quiet",
        title: "Make the picture fit another size",
        onClick: () => this.setState({
          mediaEdit: {
            kind: "resize",
            url: pick.imageUrl
          }
        })
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "aspect_ratio"), "Resize"));
    }
    if (social && v && pick.videoUrl) {
      tools.push(/*#__PURE__*/React.createElement("button", {
        key: "ve",
        className: "bm-btn sm quiet",
        title: "Captions, music and end card",
        onClick: () => this.openMedia({
          kind: "vedit",
          url: pick.videoUrl,
          onDone: async url => {
            await b.setSocialMedia(v, {
              videoUrl: url
            });
          }
        })
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "movie_edit"), "Edit video"));
      tools.push(/*#__PURE__*/React.createElement("button", {
        key: "t",
        className: "bm-btn sm quiet",
        title: "Trim or extend",
        onClick: () => this.openMedia({
          kind: "trim",
          url: pick.videoUrl,
          onDone: async url => {
            await b.setSocialMedia(v, {
              videoUrl: url
            });
          }
        })
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "content_cut"), "Trim"));
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, tools.length > 0 && /*#__PURE__*/React.createElement("div", {
      className: "bm-mediatools"
    }, tools), /*#__PURE__*/React.createElement("div", {
      className: "bm-card soft hub-make-stage",
      ref: el => {
        this._textCard = el;
      },
      onClickCapture: e => {
        if (v && this.pickQuote(p, window.getSelection(), this._textCard)) e.stopPropagation();
      }
    }, v ? /*#__PURE__*/React.createElement(PiecePreview, {
      channel: p.channel,
      title: v.title,
      body: v.body,
      imageUrl: pick.videoUrl ? null : pick.imageUrl,
      videoUrl: pick.videoUrl || null,
      posterUrl: pick.videoUrl && b.posterFor ? b.posterFor(pick.videoUrl) : null,
      link: link,
      images: b.storyImages().slice(0, 8),
      editable: true,
      onEditBody: saveBody,
      onMediaClick: social ? () => this.setState({
        pick: {
          key: p.key,
          i: -1
        },
        edit: null,
        gen: null,
        popAt: {
          top: 8,
          left: 14
        }
      }) : undefined
    }) : /*#__PURE__*/React.createElement("div", {
      className: "bm-card-wait plain"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "edit_note"), "Not written yet. Use Write this ", kindOf(p)[1], " on the right, then adjust it here."), this.renderSocialPop(p, v, pick)), v && /*#__PURE__*/React.createElement("p", {
      className: "bm-stage-hint"
    }, social ? "Click the words to edit them; click the picture to swap it." : "Click the words to edit them. Pick a layout above to edit it part by part."));
  }
  renderSocialPop(p, v, pick) {
    const b = this.props.b;
    const e = this.state.edit;
    const pk = this.state.pick;
    const g = this.state.gen;
    const which = e && e.key === p.key && e.i === -1 ? "edit" : g && g.key === p.key && g.i === -1 ? "gen" : pk && pk.key === p.key && pk.i === -1 ? "pick" : null;
    if (!which || !v) return null;
    const at = this.state.popAt || {
      top: 44,
      left: 14
    };
    let body;
    if (which === "edit") {
      const f = e.form;
      const setF = patch => this.setState({
        edit: {
          ...this.state.edit,
          form: {
            ...f,
            ...patch
          }
        }
      });
      body = /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("input", {
        value: f.title,
        placeholder: "Title",
        onChange: ev => setF({
          title: ev.target.value
        }),
        style: {
          ...INPUT,
          height: "32px",
          padding: "0 9px",
          marginTop: 4
        }
      }), /*#__PURE__*/React.createElement("textarea", {
        rows: 8,
        value: f.body,
        onChange: ev => setF({
          body: ev.target.value
        }),
        style: {
          ...INPUT,
          marginTop: 4,
          resize: "vertical"
        }
      }), /*#__PURE__*/React.createElement("div", {
        style: {
          display: "flex",
          gap: 6,
          marginTop: 8
        }
      }, /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary",
        onClick: async () => {
          if (!(await b.saveText(v, {
            title: f.title,
            body: f.body
          }))) return;
          await b.reloadDetails();
          this.setState({
            edit: null
          });
        }
      }, "Save"), /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-sm k-rounded-md k-button-flat",
        onClick: this.closePop
      }, "Cancel")));
    } else if (which === "gen") body = this.renderGenPopover(p, -1);else body = /*#__PURE__*/React.createElement(MediaPicker, {
      b: b,
      current: pick.videoUrl || pick.imageUrl || null,
      allowVideo: true,
      drafts: this.pieces().filter(x => x.kind === "draft" && x.key !== p.key),
      useOn: (u, k) => this.useImageOn(u, k),
      onPick: async x => {
        await b.setSocialMedia(v, x.kind === "video" ? {
          videoUrl: x.url,
          imageUrl: null
        } : {
          imageUrl: x.url,
          videoUrl: null
        });
        this.setState({
          pick: null
        });
      }
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pop" + (which === "pick" ? " wide" : ""),
      style: {
        top: at.top,
        left: at.left
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pop-head"
    }, /*#__PURE__*/React.createElement("b", null, OWNED.has(p.channel) ? "Copy" : "Post"), /*#__PURE__*/React.createElement("span", null, which === "edit" ? "Edit the title and caption" : which === "gen" ? "Generate an image" : "Pick an image or video"), /*#__PURE__*/React.createElement("button", {
      className: "material-icons",
      title: "Close",
      onClick: this.closePop
    }, "close")), body);
  }
  // ---- Video extras (DS 2026-10-02): thumbnail, every format, captions preview, post a clip with a caption from its transcript
  formatsOf(out) {
    const b = this.props.b;
    const vids = b.renderedVideos();
    const has = a => vids.some(x => String(x.v.body || "").includes(a) && x.url !== out);
    return {
      "9:16": has("9:16"),
      "1:1": has("1:1"),
      "4:5": has("4:5")
    };
  }
  // Sizes still to make: not resized from this video yet, and not already covered by another video row of the
  // story that has its own video (a Reel row with a video already IS the tall size — 2026-10-16, no duplicates).
  missingFormats(out) {
    const f = this.formatsOf(out);
    const b = this.props.b;
    const covered = new Set((b.state.plan || []).filter(r => r.on && r.kind === "media" && r.aspect && b.state.mediaOut[r.key] && b.state.mediaOut[r.key] !== out).map(r => r.aspect));
    return ["9:16", "1:1", "4:5"].filter(a => !f[a] && !covered.has(a));
  }
  socialDrafts() {
    return this.pieces().filter(x => x.kind === "draft" && !OWNED.has(x.channel));
  }
  renderVideoTools(p, r, out) {
    const b = this.props.b;
    const drafts = this.socialDrafts();
    const poster = b.posterFor ? b.posterFor(out) : null;
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-make-vtools"
    }, /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle xs",
      onClick: () => this.setState({
        framePick: {
          key: r.key
        }
      })
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "image"), poster ? "Change thumbnail" : "Pick a thumbnail"), drafts.length > 0 && /*#__PURE__*/React.createElement("select", {
      className: "hub-make-useon static",
      value: "",
      title: "Attach this clip to a social post and write its caption from the transcript",
      onChange: e => {
        const k = e.target.value;
        if (k) this.postClip(out, k);
      }
    }, /*#__PURE__*/React.createElement("option", {
      value: ""
    }, "Post this clip on\u2026"), drafts.map(x => /*#__PURE__*/React.createElement("option", {
      key: x.key,
      value: x.key
    }, x.label, this.variantFor(x) ? "" : " (new)"))), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: "0 0 0 auto"
      }
    }, poster ? "thumbnail set" : "no thumbnail yet: the picker and YouTube show a blank cover"));
  }
  renderCaptionPreview(p, r, sb) {
    const b = this.props.b;
    const brand = b.state.brand || {};
    const mode = b.state.bs.captions === "off" ? "off" : "brand";
    const scene = sb && sb.key === r.key ? sb.scenes.find(x => x.imageUrl) : null;
    const img = scene && scene.imageUrl || b.storyImages()[0] || null;
    const line = scene && (scene.overlayText || scene.narration) || "Captions appear like this";
    const font = String(brand.captionFont || "").includes("Serif") ? "serif" : String(brand.captionFont || "").includes("Mono") ? "monospace" : "sans-serif";
    const op = Math.round(Number(brand.captionBackOpacity == null ? 0.65 : brand.captionBackOpacity) * 255).toString(16).padStart(2, "0");
    const pos = brand.captionPosition || "bottom";
    const size = Number(brand.captionSize || 0.044);
    const set = captions => {
      b.setState({
        bs: {
          ...b.state.bs,
          captions
        }
      });
      b.saveState({
        captions
      });
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "hub-make-capprev"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-make-capprev-bar"
    }, /*#__PURE__*/React.createElement("span", {
      className: "k"
    }, "Captions"), /*#__PURE__*/React.createElement("span", {
      className: "hub-ship-seg"
    }, [["brand", "Brand preset"], ["off", "Off"]].map(([k, l]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      className: "territory-toggle xs" + (mode === k ? " active" : ""),
      onClick: () => set(k)
    }, l))), /*#__PURE__*/React.createElement("a", {
      className: "territory-hint",
      style: {
        margin: "0 0 0 auto",
        color: "var(--plk-info-text)",
        cursor: "pointer"
      },
      onClick: () => b.props.history.push("/Settings/Brand")
    }, "Change the preset")), /*#__PURE__*/React.createElement("div", {
      className: "hub-make-capprev-frame " + (r.aspect === "9:16" ? "tall" : r.aspect === "1:1" ? "square" : "wide"),
      style: {
        backgroundImage: img ? `url(${img})` : undefined,
        alignItems: pos === "top" ? "flex-start" : pos === "center" ? "center" : "flex-end"
      }
    }, brand.logoUrl && /*#__PURE__*/React.createElement("img", {
      className: "logo",
      src: brand.logoUrl,
      alt: ""
    }), mode === "brand" && /*#__PURE__*/React.createElement("span", {
      className: "cap",
      style: {
        fontFamily: font,
        color: brand.captionTextColor || "#fff",
        background: `${brand.captionBackColor || "#000000"}${op}`,
        fontSize: `${Math.max(9, Math.round(size * 260))}px`
      }
    }, String(line).slice(0, 70)), mode === "off" && /*#__PURE__*/React.createElement("span", {
      className: "off"
    }, "no captions")), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "4px 0 0"
      }
    }, mode === "brand" ? "Captions, logo and end card follow your brand settings." : "The video goes out without captions; logo and end card stay.", this.templateFor(p) && (recipeOf(this.templateFor(p)).media || {}).captions === "brand" ? " This layout turns captions on." : ""));
  }
  renderVideoStage(p) {
    const b = this.props.b;
    const r = p.row;
    const out = b.state.mediaOut[r.key];
    const sb = b.state.storyboard;
    const isClip = r.media === "cinematic";
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "bm-card video hub-make-stage"
    }, out ? /*#__PURE__*/React.createElement("video", {
      className: "hub-player",
      src: out,
      poster: b.posterFor ? b.posterFor(out) : undefined,
      controls: true,
      playsInline: true,
      preload: "metadata"
    }) : /*#__PURE__*/React.createElement("div", {
      className: "bm-card-wait plain"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "movie"), isClip ? "No clip made yet. Use Make the clip on the right, or a video you already have from Assets." : sb && sb.key === r.key ? `${sb.scenes.length} scenes ready. Use Make the video on the right.` : "No video yet. Write the scenes from the story on the right and make it, or use a video you already have from Assets."), this.state.framePick && this.state.framePick.key === r.key && /*#__PURE__*/React.createElement(FramePicker, {
      url: out,
      onClose: () => this.setState({
        framePick: null
      }),
      onPoster: async posterUrl => {
        await b.setAssetPoster(out, posterUrl);
        this.setState({
          framePick: null
        });
      },
      onPickImage: () => this.setState({
        framePick: null,
        pick: {
          key: p.key,
          i: -2
        },
        popAt: {
          top: 8,
          left: 14
        }
      })
    }), this.state.pick && this.state.pick.key === p.key && this.state.pick.i === -2 && /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pop wide",
      style: {
        top: 8,
        left: 14
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pop-head"
    }, /*#__PURE__*/React.createElement("b", null, "Cover"), /*#__PURE__*/React.createElement("span", null, "Pick a picture as the cover"), /*#__PURE__*/React.createElement("button", {
      className: "material-icons",
      title: "Close",
      onClick: this.closePop
    }, "close")), /*#__PURE__*/React.createElement(MediaPicker, {
      b: b,
      allowVideo: false,
      onPick: async x => {
        this.setState({
          pick: null
        });
        await b.setAssetPoster(out, x.url);
      }
    })), this.state.vidPick === r.key && /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pop wide",
      style: {
        top: 8,
        left: 14
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pop-head"
    }, /*#__PURE__*/React.createElement("b", null, "Use a video"), /*#__PURE__*/React.createElement("span", null, "Pick one from Assets: it becomes this piece's video"), /*#__PURE__*/React.createElement("button", {
      className: "material-icons",
      title: "Close",
      onClick: () => this.setState({
        vidPick: null
      })
    }, "close")), /*#__PURE__*/React.createElement(MediaPicker, {
      b: b,
      allowVideo: true,
      initialKind: "video",
      initialScope: "all",
      current: out || null,
      onPick: x => this.useAssetVideo(r, x)
    })), this.state.scenePick != null && sb && sb.key === r.key && /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pop wide",
      style: {
        top: 8,
        left: 14
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-make-pop-head"
    }, /*#__PURE__*/React.createElement("b", null, "Scene ", this.state.scenePick + 1), /*#__PURE__*/React.createElement("span", null, "Pick its picture"), /*#__PURE__*/React.createElement("button", {
      className: "material-icons",
      title: "Close",
      onClick: () => this.setState({
        scenePick: null
      })
    }, "close")), /*#__PURE__*/React.createElement(MediaPicker, {
      b: b,
      allowVideo: false,
      current: (sb.scenes[this.state.scenePick] || {}).imageUrl,
      onPick: x => {
        const i = this.state.scenePick;
        b.setSb(sb.scenes.map((sc, k) => k === i ? {
          ...sc,
          imageUrl: x.url
        } : sc));
        this.setState({
          scenePick: null
        });
      }
    }))), out && this.renderVideoTools(p, r, out));
  }
  renderVideoControls(p) {
    const b = this.props.b;
    const r = p.row;
    const out = b.state.mediaOut[r.key];
    const sb = b.state.storyboard;
    const isClip = r.media === "cinematic";
    const hasSb = !!(sb && sb.key === r.key);
    const bz = b.state.busy || {};
    const left = out && r.aspect === "16:9" ? this.missingFormats(out) : [];
    return /*#__PURE__*/React.createElement("div", {
      className: "bm-panel-in"
    }, /*#__PURE__*/React.createElement("div", {
      className: "bm-sec"
    }, isClip ? /*#__PURE__*/React.createElement("button", {
      className: "bm-approve",
      disabled: !!bz[`clip:${r.key}`],
      onClick: () => b.makeClip(r)
    }, bz[`clip:${r.key}`] ? "Making the clip…" : out ? "Make the clip again" : "Make the clip") : hasSb ? /*#__PURE__*/React.createElement("button", {
      className: "bm-approve",
      disabled: !!bz[`rv:${r.key}`] || !sb.scenes.some(x => (x.narration || "").trim()),
      onClick: () => b.renderVideo(r)
    }, bz[`rv:${r.key}`] ? "Making the video…" : out ? "Make the video again" : "Make the video") : /*#__PURE__*/React.createElement("button", {
      className: "bm-approve",
      disabled: !!bz[`sb:${r.key}`],
      onClick: () => b.writeStoryboard(r)
    }, bz[`sb:${r.key}`] ? "Writing the scenes…" : "Write the scenes"), /*#__PURE__*/React.createElement("span", {
      className: "bm-sub"
    }, r.aspect ? `${r.aspect === "9:16" ? "Tall" : r.aspect === "1:1" ? "Square" : r.aspect === "4:5" ? "Portrait" : "Wide"} (${r.aspect})` : "", r.prompt ? `${r.aspect ? " · " : ""}${r.prompt.slice(0, 80)}` : ""), /*#__PURE__*/React.createElement("button", {
      className: "bm-link",
      title: "Use a finished video you already have instead of making one",
      onClick: () => this.setState({
        vidPick: r.key
      })
    }, out ? "Use a different video from Assets" : "Use a video from Assets"), !isClip && hasSb && /*#__PURE__*/React.createElement("button", {
      className: "bm-link",
      disabled: !!bz[`sb:${r.key}`],
      onClick: () => b.writeStoryboard(r)
    }, bz[`sb:${r.key}`] ? "Writing…" : "Write the scenes again")), out && (() => {
      const vv = this.videoVariantFor(p);
      const vs = String(vv && vv.status || "").toLowerCase();
      if (!vv) return /*#__PURE__*/React.createElement("div", {
        className: "bm-sec"
      }, /*#__PURE__*/React.createElement("button", {
        className: "bm-approve",
        onClick: () => this.approveVideo(p, null)
      }, "Approve this video"), /*#__PURE__*/React.createElement("span", {
        className: "bm-sub"
      }, "Approved videos go to Publish with the other pieces."));
      const vt = (this.state.vtext || {})[String(vv.id)] || {
        title: vv.title || "",
        body: b.captionText(vv.body || "")
      };
      const setVt = patch => this.setState({
        vtext: {
          ...(this.state.vtext || {}),
          [String(vv.id)]: {
            ...vt,
            ...patch
          }
        }
      });
      const dirty = vt.title !== (vv.title || "") || vt.body !== b.captionText(vv.body || "");
      const saveVt = async () => {
        if (!(await b.saveText(vv, {
          title: vt.title.trim(),
          body: vt.body.trim()
        }))) return;
        await b.reloadDetails();
        const n = {
          ...(this.state.vtext || {})
        };
        delete n[String(vv.id)];
        this.setState({
          vtext: n
        });
        toast("Title and description saved.");
      };
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: "bm-sec"
      }, /*#__PURE__*/React.createElement("b", {
        className: "bm-h"
      }, "Title and description"), /*#__PURE__*/React.createElement("span", {
        className: "bm-sub left"
      }, "YouTube uses both; Facebook, Instagram and TikTok use the description as the caption."), /*#__PURE__*/React.createElement("input", {
        value: vt.title,
        maxLength: 100,
        placeholder: "Title",
        onChange: e => setVt({
          title: e.target.value
        }),
        style: {
          height: 34,
          padding: "0 10px",
          border: "1px solid var(--bm-edge)",
          borderRadius: 8,
          font: "inherit",
          fontSize: 13
        }
      }), /*#__PURE__*/React.createElement("textarea", {
        value: vt.body,
        rows: 6,
        placeholder: "Description: what the video shows, a link, hashtags",
        onChange: e => setVt({
          body: e.target.value
        }),
        style: {
          padding: "8px 10px",
          border: "1px solid var(--bm-edge)",
          borderRadius: 8,
          font: "inherit",
          fontSize: 13,
          resize: "vertical"
        }
      }), dirty && /*#__PURE__*/React.createElement("div", {
        className: "bm-row-acts"
      }, /*#__PURE__*/React.createElement("button", {
        className: "bm-btn sm",
        onClick: saveVt
      }, "Save"), /*#__PURE__*/React.createElement("button", {
        className: "bm-link mute",
        onClick: () => {
          const n = {
            ...(this.state.vtext || {})
          };
          delete n[String(vv.id)];
          this.setState({
            vtext: n
          });
        }
      }, "Undo"))), /*#__PURE__*/React.createElement("div", {
        className: "bm-sec"
      }, vs === "approved" ? /*#__PURE__*/React.createElement("div", {
        className: "bm-done"
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "check_circle"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "Approved"), /*#__PURE__*/React.createElement("i", null, "Ready to publish.")), /*#__PURE__*/React.createElement("button", {
        className: "bm-link",
        onClick: () => b.setVariantStatus(vv, "draft")
      }, "Back to review")) : vs === "published" || vs === "live" ? /*#__PURE__*/React.createElement("div", {
        className: "bm-done"
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons"
      }, "check_circle"), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("b", null, "Published"), /*#__PURE__*/React.createElement("i", null, "It can go out again from Publish."))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
        className: "bm-approve",
        onClick: () => this.approveVideo(p, vv)
      }, "Approve this video"), /*#__PURE__*/React.createElement("span", {
        className: "bm-sub"
      }, "Approved videos go to Publish with the other pieces."))));
    })(), out && /*#__PURE__*/React.createElement("div", {
      className: "bm-sec"
    }, /*#__PURE__*/React.createElement("b", {
      className: "bm-h"
    }, "Adjust"), /*#__PURE__*/React.createElement("div", {
      className: "bm-row-acts wrap"
    }, /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm",
      title: "Captions, music and end card",
      onClick: () => this.openMedia({
        kind: "vedit",
        url: out,
        onDone: url => b.replaceVideoFile(r.key, url)
      })
    }, "Edit"), /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm",
      title: "Trim or extend",
      onClick: () => this.openMedia({
        kind: "trim",
        url: out,
        onDone: url => b.replaceVideoFile(r.key, url)
      })
    }, "Trim"), /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm",
      title: "Make it fit other sizes",
      onClick: () => this.openMedia({
        kind: "vresize",
        url: out
      })
    }, "Resize")), r.aspect === "16:9" && /*#__PURE__*/React.createElement("button", {
      className: "bm-link",
      title: "Tall for Reels, Shorts and TikTok; square and portrait for the feed. Each is saved under this story.",
      disabled: !!bz[`rf:${r.key}`] || !left.length,
      onClick: () => this.allFormats(r, out)
    }, bz[`rf:${r.key}`] ? "Making the other sizes…" : left.length ? `Make the other sizes (${left.length} to go)` : "Every size is made")), !isClip && this.fold(`voice:${r.key}`, "Voice and captions", null, /*#__PURE__*/React.createElement("div", {
      className: "hub-make-fields"
    }, /*#__PURE__*/React.createElement(VoicePicker, {
      value: b.state.bs.voice || "Joanna",
      selectStyle: {
        ...INPUT,
        height: 32,
        padding: "0 8px"
      },
      onChange: voice => {
        b.setState({
          bs: {
            ...b.state.bs,
            voice
          }
        });
        b.saveState({
          voice
        });
      }
    }), /*#__PURE__*/React.createElement("label", null, "Music ", /*#__PURE__*/React.createElement("i", null, "optional link to an mp3, played under the voice"), /*#__PURE__*/React.createElement("input", {
      value: b.state.bs.musicUrl || "",
      placeholder: "https://\u2026 .mp3",
      onChange: e => {
        const musicUrl = e.target.value;
        b.setState({
          bs: {
            ...b.state.bs,
            musicUrl
          }
        });
      },
      onBlur: () => b.saveState({
        musicUrl: b.state.bs.musicUrl || ""
      }),
      style: {
        ...INPUT,
        height: 32,
        padding: "0 8px",
        marginTop: 4
      }
    })), this.renderCaptionPreview(p, r, sb))), hasSb && this.fold(`scenes:${r.key}`, "Scenes", String(sb.scenes.length), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
      className: "bm-sub left",
      style: {
        margin: "0 0 6px"
      }
    }, "Change what is said, swap or remake a picture, add words on screen, reorder or drop scenes. The video uses exactly these."), sb.scenes.map((sc, i) => /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "hub-make-scene"
    }, /*#__PURE__*/React.createElement("div", {
      className: "hub-make-scene-img pickable",
      title: "Click to pick another picture",
      onClick: () => this.setState({
        scenePick: i
      })
    }, sc.imageUrl ? /*#__PURE__*/React.createElement("img", {
      src: sc.imageUrl,
      alt: ""
    }) : /*#__PURE__*/React.createElement("span", {
      className: "material-icons",
      style: {
        fontSize: 22,
        color: "var(--plk-text-subtle)",
        display: "block",
        textAlign: "center",
        lineHeight: "64px"
      }
    }, "add_photo_alternate"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, i + 1), /*#__PURE__*/React.createElement("span", {
      className: "material-icons sw"
    }, "photo_library")), /*#__PURE__*/React.createElement("div", {
      className: "hub-make-scene-text"
    }, /*#__PURE__*/React.createElement("textarea", {
      rows: 2,
      value: sc.narration,
      placeholder: "What the voice says in this scene",
      onChange: e => b.setSb(sb.scenes.map((x, k) => k === i ? {
        ...x,
        narration: e.target.value
      } : x)),
      style: {
        ...INPUT,
        resize: "vertical",
        fontSize: 12,
        minHeight: 52,
        height: 52
      }
    }), /*#__PURE__*/React.createElement("input", {
      value: sc.overlayText || "",
      placeholder: "Words on screen (optional)",
      onChange: e => b.setSb(sb.scenes.map((x, k) => k === i ? {
        ...x,
        overlayText: e.target.value
      } : x)),
      style: {
        ...INPUT,
        height: 28,
        padding: "0 8px",
        fontSize: 11.5,
        marginTop: 4
      }
    })), /*#__PURE__*/React.createElement("div", {
      className: "hub-make-scene-acts"
    }, /*#__PURE__*/React.createElement("button", {
      title: "Rewrite what is said",
      disabled: !!bz[`sc:w:${i}`],
      onClick: () => b.rewriteScene(i)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "auto_fix_high")), /*#__PURE__*/React.createElement("button", {
      title: "Make a new picture for this scene",
      disabled: !!bz[`sc:i:${i}`],
      onClick: () => b.regenSceneImage(i)
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "image")), /*#__PURE__*/React.createElement("button", {
      title: "Move up",
      disabled: i === 0,
      onClick: () => {
        const n = sb.scenes.slice();
        [n[i - 1], n[i]] = [n[i], n[i - 1]];
        b.setSb(n);
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "arrow_upward")), /*#__PURE__*/React.createElement("button", {
      title: "Move down",
      disabled: i === sb.scenes.length - 1,
      onClick: () => {
        const n = sb.scenes.slice();
        [n[i + 1], n[i]] = [n[i], n[i + 1]];
        b.setSb(n);
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "arrow_downward")), /*#__PURE__*/React.createElement("button", {
      title: "Remove this scene",
      className: "danger",
      disabled: sb.scenes.length <= 1,
      onClick: () => b.setSb(sb.scenes.filter((_, k) => k !== i))
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, "close"))))), /*#__PURE__*/React.createElement("div", {
      className: "bm-row-acts"
    }, /*#__PURE__*/React.createElement("button", {
      className: "bm-btn sm",
      disabled: sb.scenes.length >= 8,
      onClick: () => {
        const imgs = b.storyImages();
        b.setSb([...sb.scenes, {
          imageUrl: imgs[sb.scenes.length % Math.max(imgs.length, 1)] || "",
          narration: "",
          overlayText: ""
        }]);
      }
    }, "Add a scene"))), true));
  }
}